import { exportXLSX } from "./exportXLSX";
import { useAcl } from "./context/AclContext";
import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, IconButton, TextField, Alert,
  Chip, Breadcrumbs, Link, LinearProgress, Button, TableSortLabel, Dialog, DialogContent, DialogTitle, Tooltip,
  Select, MenuItem, Backdrop, CircularProgress, Tabs, Tab
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import RefreshIcon from "@mui/icons-material/Refresh";
import DownloadIcon from "@mui/icons-material/Download";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import HandshakeIcon from "@mui/icons-material/Handshake";
import BadgeIcon from "@mui/icons-material/Badge";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import SearchIcon from "@mui/icons-material/Search";
import MapIcon from "@mui/icons-material/Map";
import TimelineIcon from "@mui/icons-material/Timeline";
import InfoIcon from "@mui/icons-material/Info";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import { MapContainer, TileLayer, Marker, Popup, GeoJSON } from "react-leaflet";
import L from "leaflet";
import RouteMapMarkers, { getRouteSections } from "./components/RouteMapMarkers";
import "leaflet/dist/leaflet.css";
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, ResponsiveContainer } from "recharts";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
});

const API = process.env.REACT_APP_API_URL || "";

// Oculta temporalmente la categoría "Afiliados" (sin credencial) en reportes,
// exportaciones y totales. Cambiar a true cuando deba volver a mostrarse.
const SHOW_PLAIN_AFFILIATES = false;

const getColor = (val, min, max) => {
  if (val == null || max === min) return "#9e9e9e";
  const ratio = (val - min) / (max - min);
  if (ratio >= 0.75) return "#2e7d32";
  if (ratio >= 0.5) return "#689f38";
  if (ratio >= 0.25) return "#f9a825";
  return "#c62828";
};

function exportCSV(rows, columns, labels, filename) {
  const header = labels.join(",");
  const csv = [header, ...rows.map(r => columns.map(c => `"${String(r[c] ?? "").replace(/"/g, '""')}"`).join(","))].join("\n");
  const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

const num = (v) => v != null ? Number(v).toLocaleString("es-MX") : "0";
const pct = (v) => v != null ? `${Number(v).toFixed(1)}%` : "0%";
const fechaCompleta = (value) => {
  if (!value) return "";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? String(value)
    : date.toLocaleDateString("es-MX", {
        day: "numeric", month: "long", year: "numeric",
      }) + " a las " + date.toLocaleTimeString("es-MX", {
        hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: true,
      });
};
const fechaRelativa = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  const ahora = new Date();
  const diferenciaMs = ahora.getTime() - date.getTime();
  if (diferenciaMs < 0) return fechaCompleta(value);
  const minutos = Math.floor(diferenciaMs / 60000);
  if (minutos < 1) return "Ahora";
  if (minutos < 60) return `Hace ${minutos} ${minutos === 1 ? "minuto" : "minutos"}`;
  const horas = Math.floor(minutos / 60);
  if (horas < 24) return `Hace ${horas} ${horas === 1 ? "hora" : "horas"}`;
  const inicioHoy = new Date(ahora.getFullYear(), ahora.getMonth(), ahora.getDate());
  const inicioFecha = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const diasCalendario = Math.round((inicioHoy - inicioFecha) / 86400000);
  if (diasCalendario === 1) return "Ayer";
  if (diasCalendario < 7) return `Hace ${diasCalendario} días`;
  return date.toLocaleDateString("es-MX", { day: "2-digit", month: "2-digit", year: "numeric" });
};
const sentenceCase = (value) => {
  const text = String(value || "").trim().toLocaleLowerCase("es-MX");
  return text ? text.charAt(0).toLocaleUpperCase("es-MX") + text.slice(1) : text;
};
const normalizeTerritoryNames = (row) => ({
  ...row,
  ...(row.entidad_nombre ? { entidad_nombre: sentenceCase(row.entidad_nombre) } : {}),
  ...(row.entidades_nombres ? {
    entidades_nombres: row.entidades_nombres.split(",").map((name) => sentenceCase(name)).join(", "),
  } : {}),
});
const splitRouteName = (value) => {
  const name = String(value || "").trim();
  if (!name || name === "Rutas activas" || name === "Rutas vacantes") return null;
  const match = name.match(/^(.*)_([^_]+)$/);
  return match ? { prefix: match[1], number: match[2] } : { prefix: name, number: name };
};
const headerSx = { fontWeight: 700, bgcolor: "#AF272F", color: "#fff" };
const sortSx = {"& .MuiTableSortLabel-icon":{color:"#fff !important"},color:"#fff !important"};

function AvanceDiarioCard({ kpis, esHoy, pctAvance, pctExito, filtroOperacion, onFiltroOperacionChange, mostrarDistribucion = true }) {
  const convencidosDia = esHoy ? kpis?.convencidos_hoy : kpis?.convencidos_ayer;
  const faltaronDia = esHoy ? kpis?.faltaron_hoy : kpis?.faltaron_ayer;
  const promedioVisitasDia = esHoy ? kpis?.promedio_visitas_cot_hoy : kpis?.promedio_visitas_cot_ayer;
  const visitasDia = esHoy ? kpis?.visitas_hoy : kpis?.visitas_ayer;
  const cotsConVisitaDia = esHoy ? kpis?.cots_con_visita_hoy : kpis?.cots_con_visita_ayer;
  const visitasRechazadasDia = Math.max(
    Number(visitasDia || 0) - Number(convencidosDia || 0),
    0,
  );
  const distribucionCots = esHoy ? kpis?.distribucion_cots_hoy : kpis?.distribucion_cots_ayer;
  const distribucionData = Array.from({ length: 16 }, (_, index) => ({
    rango: index === 15 ? "15+" : String(index),
    cots: Number(distribucionCots?.[index] || 0),
  }));

  return (
    <Box sx={{ flex: "1 1 450px", minWidth: 0, minHeight: { sm: 320 }, p: { xs: 2.5, sm: 3.25 }, borderRadius: 2.5, bgcolor: "#fffaf2", border: "1px solid #ead8b9", boxShadow: "0 4px 14px rgba(188,149,91,0.09)" }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, flexWrap: "wrap", mb: 1.5 }}>
        <Typography variant="h6" fontWeight={800} sx={{ color: "#BC955B" }}>
          Avance de convencidos
        </Typography>
        <Select
          size="small"
          value={filtroOperacion}
          onChange={(event) => onFiltroOperacionChange(event.target.value)}
          sx={{ minWidth: 145, height: 30, borderRadius: 16, color: "#BC955B", fontSize: "0.78rem", fontWeight: 700, "& .MuiOutlinedInput-notchedOutline": { borderColor: "#BC955B" }, "& .MuiSelect-select": { py: 0.5, pl: 1.5 }, "& .MuiSelect-icon": { color: "#BC955B" } }}
        >
          <MenuItem value="general">General</MenuItem>
          <MenuItem value="casa">Casa x casa</MenuItem>
          <MenuItem value="credencial">Afiliados con Credencial</MenuItem>
        </Select>
      </Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 2, flexWrap: "wrap", mb: 1 }}>
        <Typography variant="h5" fontWeight={700}>
          {num(convencidosDia)} <Typography component="span" variant="body1" color="text.secondary">convencidos de {num(kpis?.meta_diaria)} esperados</Typography>
          <Tooltip
            arrow
            placement="top"
            title={`La meta diaria se calcula multiplicando las rutas con un COT responsable asignado por 15 convencidos esperados por día: ${num(kpis?.rutas_asignadas)} rutas × 15 = ${num(kpis?.meta_diaria)} esperados.`}
          >
            <InfoIcon sx={{ ml: 0.5, fontSize: 18, color: "#777", verticalAlign: "middle", cursor: "help" }} />
          </Tooltip>
        </Typography>
        <Typography variant="h6" fontWeight={700} sx={{ color: "#BC955B" }}>
          {pctAvance}% de avance
        </Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={Math.min(100, Math.max(0, Number(pctAvance)))}
        sx={{ height: 12, borderRadius: 6, bgcolor: "#f3eadc", "& .MuiLinearProgress-bar": { bgcolor: "#BC955B", borderRadius: 6 } }}
      />
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
        {esHoy ? "Faltan" : "Faltaron"} {num(faltaronDia)} convencidos para alcanzar la meta diaria
      </Typography>
      {mostrarDistribucion && <Box sx={{ mt: 2.5, pt: 2, borderTop: "1px solid #eadfce" }}>
        <Typography variant="subtitle1" fontWeight={800} sx={{ color: "#6b4d22" }}>
          Distribución de COTs por convencidos
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          COTs agrupados según el número de convencidos registrados en el día.
        </Typography>
        <Box sx={{ width: "100%", height: 225 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={distribucionData} margin={{ top: 10, right: 8, left: -18, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eadfce" />
              <XAxis dataKey="rango" tick={{ fontSize: 11 }} tickLine={false} axisLine={{ stroke: "#d9c7aa" }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
              <RTooltip
                cursor={{ fill: "rgba(188,149,91,0.08)" }}
                formatter={(value) => [num(value), "COTs"]}
                labelFormatter={(label) => `${label} convencidos`}
              />
              <Bar dataKey="cots" name="COTs" fill="#BC955B" radius={[4, 4, 0, 0]} maxBarSize={28} />
            </BarChart>
          </ResponsiveContainer>
        </Box>
      </Box>}
      {/* Indicadores secundarios ocultos temporalmente a solicitud de diseño.
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" }, gap: 1.5, mt: 2 }}>
        <Box sx={{ p: 1, bgcolor: "#f5f5f5", borderRadius: 1 }}>
          <Typography variant="h6" fontWeight={700}>{num(visitasDia)}</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", lineHeight: 1.4 }}>Visitas realizadas</Typography>
        </Box>
        <Box sx={{ p: 1, bgcolor: "#fff3f3", borderRadius: 1 }}>
          <Typography variant="h6" fontWeight={700} sx={{ color: "#c62828" }}>{num(visitasRechazadasDia)}</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", lineHeight: 1.4 }}>Visitas rechazadas</Typography>
        </Box>
        <Box sx={{ p: 1, bgcolor: "#e8f5e9", borderRadius: 1 }}>
          <Typography variant="h6" fontWeight={700} sx={{ color: "#2e7d32" }}>{pctExito}%</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", lineHeight: 1.4 }}>Éxito de visitas</Typography>
        </Box>
        <Box sx={{ p: 1, bgcolor: "#f5f5f5", borderRadius: 1 }}>
          <Typography variant="h6" fontWeight={700}>{num(cotsConVisitaDia)}</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", lineHeight: 1.4 }}>COTs con visitas registradas</Typography>
        </Box>
        <Box sx={{ p: 1, bgcolor: "#f5f5f5", borderRadius: 1 }}>
          <Typography variant="h6" fontWeight={700}>{promedioVisitasDia || 0}</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", lineHeight: 1.4 }}>Promedio de visitas por COT activo</Typography>
        </Box>
        <Box sx={{ p: 1, bgcolor: "#f5f5f5", borderRadius: 1 }}>
          <Typography variant="h6" fontWeight={700}>{kpis?.ritmo_por_cot_dia || 0}</Typography>
        </Box>
      </Box>
      */}
    </Box>
  );
}

function ReportesPromueve({ token }) {
  const acl = useAcl() || { can: () => true, loading: false };
  const aclLoading = acl.loading;
  const canProgreso = aclLoading || acl.can("promueve.avance.progreso");
  const canProgresoMeta = aclLoading || acl.can("promueve.avance.progreso.meta");
  const canProgresoDetalle = aclLoading || acl.can("promueve.avance.progreso.detalle");
  const canOperacion = aclLoading || acl.can("promueve.avance.operacion");
  const canOperacionCotsActivos = aclLoading || acl.can("promueve.avance.operacion.cotsactivos");
  const canOperacionRutasActivas = aclLoading || acl.can("promueve.avance.operacion.rutasactivas");
  const canOperacionAvanceMeta = aclLoading || acl.can("promueve.avance.operacion.avancemeta");
  const canOperacionDistribucion = aclLoading || acl.can("promueve.avance.operacion.distribucion");
  const canOperacionHoy = aclLoading || acl.can("promueve.avance.operacion.hoy");
  const canOperacionUltimoDia = aclLoading || acl.can("promueve.avance.operacion.ultimodia");
  const canTops = aclLoading || acl.can("promueve.avance.tops");
  const canTabla = aclLoading || acl.can("promueve.avance.tabla");
  const canTablaAmpliar = aclLoading || acl.can("promueve.avance.tabla.ampliar");
  const canTablaMapaRuta = aclLoading || acl.can("promueve.avance.tabla.maparuta");
  const canTablaAvanceGrafica = aclLoading || acl.can("promueve.avance.tabla.avancegrafica");
  const canDescargaExcel = aclLoading || acl.can("promueve.avance.general.descarga");
  const canCotsNuevos = aclLoading || acl.can("promueve.avance.general.cotsnuevos");
  const canCotsViejos = aclLoading || acl.can("promueve.avance.general.cotsviejos");
  const canCotsContact = canCotsNuevos || canCotsViejos;
  const [view, setView] = useState("entidad");
  const [data, setData] = useState([]);
  const [selectedEntidad, setSelectedEntidad] = useState(null);
  const [selectedDistrito, setSelectedDistrito] = useState(null);
  const [selectedUsuario, setSelectedUsuario] = useState(null);
  const [selectedMunicipio, setSelectedMunicipio] = useState(null);
  const [distritoView, setDistritoView] = useState("rutas");
  const [visitas, setVisitas] = useState([]);
  const [convencidos, setConvencidos] = useState([]);
  const [secciones, setSecciones] = useState([]);
  const [visitasLoading, setVisitasLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("circunscripcion");
  const [sortDir, setSortDir] = useState("asc");
  const [highlightId, setHighlightId] = useState(null);
  const [modalMapa, setModalMapa] = useState(false);
  const [modalGrafica, setModalGrafica] = useState(false);
  const [modalUsuario, setModalUsuario] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [chartLoading, setChartLoading] = useState(false);
  const [chartError, setChartError] = useState("");
  const chartRequest = useRef(0);
  const [chartFechaInicio, setChartFechaInicio] = useState("");
  const [chartFechaFin, setChartFechaFin] = useState("");
  const [modo, setModo] = useState("territorio"); // "territorio" | "distrital"
  const [vistaInicial, setVistaInicial] = useState("entidad");
  const [circunscripcionActiva, setCircunscripcionActiva] = useState(null);
  const [regionActiva, setRegionActiva] = useState(null);
  const [entidadesRegion, setEntidadesRegion] = useState([]);
  const [avanceEsHoy, setAvanceEsHoy] = useState(false);
  const [tablaExpandida, setTablaExpandida] = useState(false);

  // Perfil territorial del usuario
  const perfil = JSON.parse(localStorage.getItem("perfil_territorial") || "null");
  const roleCns = JSON.parse(localStorage.getItem("roles") || "[]");
  const cargoId = Number(perfil?.cargo_id);
  const soloEstructura = (roleCns.includes("reportes_estructura") || (cargoId >= 3 && cargoId <= 7) || cargoId === 13) && !roleCns.includes("compulsa") && !roleCns.includes("monitoreo") && !roleCns.includes("nacional");
  const tipoTerritorio = (soloEstructura && perfil?.afiliacion_tipo) ? perfil.afiliacion_tipo : "NACIONAL";

  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = useCallback(async (url) => {
    setLoading(true); setError("");
    try {
      const res = await fetch(url, { headers });
      if (!res.ok) throw new Error(await res.text());
      const json = await res.json();
      setData((json.data || []).map(normalizeTerritoryNames));
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [token]);

  const fetchRegiones = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const res = await fetch(`${API}/api/promocion-usuario/por-entidad`, { headers });
      if (!res.ok) throw new Error(await res.text());
      const rows = ((await res.json()).data || []).map(normalizeTerritoryNames);
      setEntidadesRegion(rows);
      const numericKeys = [
        "total_usuarios", "meta", "total_secciones", "convencidos_total", "total_plazas", "rutas_asignadas",
        "plazas_disponibles", "conv_hoy", "meta_diaria", "faltaron_hoy", "pendientes",
        "visitas_hoy", "conv_normales", "conv_credencial", "conv_afiliado",
        "conv_total_tipos", "ritmo_necesario",
        ...[1,2,3,4,5,6,7].flatMap((day) => [
          `dia_${day}`, `dia_${day}_norm`, `dia_${day}_cred`, `dia_${day}_afil`,
        ]),
      ];
      const grouped = {};
      rows.forEach((row) => {
        const region = row.region_decimal ?? row.region ?? "Sin región";
        const item = grouped[region] || { region_decimal: region, region_nombre: `Región ${region}`, entidades: 0 };
        item.entidades += 1;
        numericKeys.forEach((key) => { item[key] = Number(item[key] || 0) + Number(row[key] || 0); });
        grouped[region] = item;
      });
      const regions = Object.values(grouped).map((item) => ({ ...item, pct_convencidos: item.meta > 0 ? (item.convencidos_total / item.meta) * 100 : 0 }));
      setData(regions.sort((a, b) => String(a.region_decimal).localeCompare(String(b.region_decimal), "es", { numeric: true })));
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [token]);

  const fetchEntidadesRegion = useCallback(async (regionDecimal) => {
    setLoading(true); setError("");
    try {
      const res = await fetch(`${API}/api/promocion-usuario/por-entidad`, { headers });
      if (!res.ok) throw new Error(await res.text());
      const rows = ((await res.json()).data || []).map(normalizeTerritoryNames);
      const rowsRegion = rows.filter((row) => String(row.region_decimal ?? row.region) === String(regionDecimal));
      setEntidadesRegion(rowsRegion);
      setData(rowsRegion);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => {
    if (tipoTerritorio === "REGIONAL" && perfil?.region_decimal != null) {
      setVistaInicial("region");
      setView("entidad");
      setRegionActiva(perfil.region_decimal);
      fetchEntidadesRegion(perfil.region_decimal);
    } else if (tipoTerritorio === "DISTRITAL" && perfil?.entidad && perfil?.distrito) {
      // El nivel raiz de un usuario distrital es el desglose de municipios
      // de su distrito; no debe entrar directamente al listado de usuarios.
      setVistaInicial("municipio");
      setView("municipio");
      setSelectedEntidad({ entidad: perfil.entidad });
      setSelectedDistrito({ distrito_f: perfil.distrito });
      fetchData(`${API}/api/promocion-usuario/por-municipio?entidad=${perfil.entidad}&distrito_f=${perfil.distrito}`);
    } else if (tipoTerritorio === "ESTATAL" && perfil?.entidad) {
      setVistaInicial("distrito");
      setView("distrito");
      setSelectedEntidad({ entidad: perfil.entidad });
      fetchData(`${API}/api/promocion-usuario/por-distrito?entidad=${perfil.entidad}`);
    } else if (tipoTerritorio === "CIRCUNSCRIPCIONAL" && perfil?.circunscripcion) {
      setVistaInicial("entidad");
      setView("entidad");
      fetchData(`${API}/api/promocion-usuario/por-entidad?circunscripcion=${perfil.circunscripcion}`);
    } else if (tipoTerritorio === "SECCIONAL" && perfil?.entidad && perfil?.distrito) {
      setVistaInicial("usuario");
      setView("usuario");
      setSelectedEntidad({ entidad: perfil.entidad });
      setSelectedDistrito({ distrito_f: perfil.distrito });
      fetchData(`${API}/api/promocion-usuario/por-usuario?entidad=${perfil.entidad}&distrito_f=${perfil.distrito}`);
    } else {
      setVistaInicial("entidad");
      fetchData(`${API}/api/promocion-usuario/por-entidad`);
    }
  }, [fetchData, fetchEntidadesRegion]);

  const handleEntidadClick = (row) => { setSelectedEntidad(row); setView("distrito"); setSearch(""); fetchData(`${API}/api/promocion-usuario/por-distrito?entidad=${row.entidad}`); };
  const handleCircunscripcionClick = (row) => {
    setCircunscripcionActiva(row.circunscripcion);
    setView("entidad");
    setModo("circunscripcion");
    setSelectedEntidad(null);
    setSelectedDistrito(null);
    setSearch("");
    fetchData(`${API}/api/promocion-usuario/por-entidad?circunscripcion=${encodeURIComponent(row.circunscripcion)}`);
  };
  const handleRegionClick = (row) => {
    setRegionActiva(row.region_decimal);
    setView("entidad");
    setSearch("");
    setSelectedEntidad(null);
    setData(entidadesRegion.filter((entidad) => String(entidad.region_decimal ?? entidad.region) === String(row.region_decimal)));
  };
  const handleDistritoClick = (row) => {
    const entidad = selectedEntidad?.entidad || row.entidad;
    setSelectedEntidad(selectedEntidad?.entidad ? selectedEntidad : { entidad, entidad_nombre: row.entidad_nombre });
    setSelectedDistrito(row);
    setDistritoView("rutas");
    setSelectedMunicipio(null);
    setSelectedUsuario(null);
    setView("plaza");
    setSearch("");
    fetchData(`${API}/api/promocion-usuario/por-plaza?entidad=${entidad}&distrito_f=${row.distrito_f}`);
  };
  const handleMunicipioClick = (row) => {
    setSelectedMunicipio(row); setView("plaza"); setSearch("");
    if (modo === "municipios") {
      setSelectedEntidad({ entidad: row.entidad, entidad_nombre: row.entidad_nombre });
      setSelectedDistrito({ distrito_f: row.distrito_f, entidad: row.entidad, entidad_nombre: row.entidad_nombre });
      fetchData(`${API}/api/promocion-usuario/por-plaza?entidad=${row.entidad}&distrito_f=${row.distrito_f}&cve_municipio_inegi=${encodeURIComponent(row.cve_municipio_inegi)}`);
    } else {
      fetchData(`${API}/api/promocion-usuario/por-plaza?entidad=${selectedEntidad.entidad}&distrito_f=${selectedDistrito.distrito_f}&cve_municipio_inegi=${encodeURIComponent(row.cve_municipio_inegi)}`);
    }
  };
  const handlePlazaClick = (row) => { setSelectedUsuario(row); setView("seccion"); setSearch(""); fetchData(`${API}/api/promocion-usuario/por-seccion-plaza?plaza_id=${row.plaza_id}${selectedMunicipio?.cve_municipio_inegi ? "&cve_municipio_inegi="+encodeURIComponent(selectedMunicipio.cve_municipio_inegi) : ""}`); };
  const handleUsuarioClick = async (row) => {
    if (!row.uid) return;
    setSelectedUsuario(row); setView("mapa"); setVisitasLoading(true);
    try {
      const res = await fetch(`${API}/api/promueve/visitas-usuario?uid=${encodeURIComponent(row.uid)}`, { headers });
      if (!res.ok) throw new Error(await res.text());
      const json = await res.json();
      setVisitas(json.data || []);
      setConvencidos(json.convencidos || []);
      setSecciones(json.secciones || []);
    } catch (e) { setError(e.message); }
    finally { setVisitasLoading(false); }
  };
  const openMapa = async (row) => {
    setModalUsuario(row); setModalMapa(true); setVisitasLoading(true);
    try {
      const res = await fetch(`${API}/api/promueve/convencidos-ruta?plaza_id=${row.plaza_id}`, { headers });
      if (!res.ok) throw new Error(await res.text());
      const json = await res.json();
      setVisitas([]);
      setConvencidos(json.convencidos || []);
      setSecciones(json.secciones || []);
    } catch (e) { setError(e.message); }
    finally { setVisitasLoading(false); }
  };
  const openGrafica = async (row) => {
    setModalUsuario(row); setModalGrafica(true); setChartData([]); setChartFechaInicio(""); setChartFechaFin("");
    fetchChartData(row.plaza_id, "", "");
  };
  const fetchChartData = async (plazaId, desde, hasta) => {
    const request = ++chartRequest.current;
    setChartError("");
    setChartData([]);
    if (desde && hasta && desde > hasta) {
      setChartLoading(false);
      setChartError("La fecha Desde no puede ser posterior a Hasta.");
      return;
    }
    setChartLoading(true);
    try {
      let url = `${API}/api/promocion-usuario/tendencia-ruta?plaza_id=${encodeURIComponent(plazaId)}`;
      if (desde) url += `&fecha_inicio=${desde}`;
      if (hasta) url += `&fecha_fin=${hasta}`;
      const res = await fetch(url, { headers });
      if (!res.ok) {
        const error = await res.json().catch(() => null);
        throw new Error(typeof error?.detail === "string" ? error.detail : "No se pudo cargar la gráfica. Intenta de nuevo.");
      }
      const json = await res.json();
      if (request === chartRequest.current) setChartData(json.data || []);
    } catch (e) {
      if (request === chartRequest.current) setChartError(e.message);
    } finally {
      if (request === chartRequest.current) setChartLoading(false);
    }
  };
  const handleBack = () => {
    if (view === "mapa") { setView("plaza"); setSelectedUsuario(null); setVisitas([]); setConvencidos([]); setSecciones([]); setSearch(""); fetchData(`${API}/api/promocion-usuario/por-plaza?entidad=${selectedEntidad.entidad}&distrito_f=${selectedDistrito.distrito_f}${selectedMunicipio?.cve_municipio_inegi ? `&cve_municipio_inegi=${encodeURIComponent(selectedMunicipio.cve_municipio_inegi)}` : ""}`); }
    else if (view === "seccion") { setView("plaza"); setSelectedUsuario(null); setSearch(""); fetchData(`${API}/api/promocion-usuario/por-plaza?entidad=${selectedEntidad.entidad}&distrito_f=${selectedDistrito.distrito_f}${selectedMunicipio?.cve_municipio_inegi ? `&cve_municipio_inegi=${encodeURIComponent(selectedMunicipio.cve_municipio_inegi)}` : ""}`); }
    else if (view === "plaza" && selectedDistrito && !selectedMunicipio) { setView("distrito"); setSelectedDistrito(null); setSelectedMunicipio(null); setSearch(""); fetchData(`${API}/api/promocion-usuario/por-distrito${selectedEntidad?.entidad ? `?entidad=${selectedEntidad.entidad}` : ""}`); }
    else if (view === "plaza" && modo === "municipios") { setView("municipio"); setSelectedEntidad(null); setSelectedMunicipio(null); setSearch(""); fetchData(`${API}/api/promocion-usuario/por-municipio`); }
    else if (view === "plaza") { setView("municipio"); setSelectedMunicipio(null); setSearch(""); fetchData(`${API}/api/promocion-usuario/por-municipio?entidad=${selectedEntidad.entidad}&distrito_f=${selectedDistrito.distrito_f}`); }
    else if (view === "municipio") {
      setView("distrito"); setSelectedDistrito(null); setSearch("");
      if (vistaInicial === "distrito") fetchData(`${API}/api/promocion-usuario/por-distrito`);
      else fetchData(`${API}/api/promocion-usuario/por-distrito?entidad=${selectedEntidad.entidad}`);
    }
    else if (view === "distrito") {
      setView("entidad"); setSelectedEntidad(null); setSearch("");
      if (vistaInicial === "circunscripcion") fetchData(`${API}/api/promocion-usuario/por-entidad?circunscripcion=${encodeURIComponent(circunscripcionActiva)}`);
      else if (vistaInicial === "region" && regionActiva != null) setData(entidadesRegion.filter((row) => String(row.region_decimal ?? row.region) === String(regionActiva)));
      else fetchData(`${API}/api/promocion-usuario/por-entidad`);
    }
    else if (view === "entidad" && vistaInicial === "region") {
      setView("region"); setRegionActiva(null); setSearch(""); fetchRegiones();
    }
    else if (view === "entidad" && vistaInicial === "circunscripcion") {
      setView("circunscripcion"); setSearch(""); fetchData(`${API}/api/promocion-usuario/por-circunscripcion`);
    }
  };
  const handleRefresh = () => {
    if (view === "region") fetchRegiones();
    else if (view === "entidad" && vistaInicial === "region" && regionActiva != null) setData(entidadesRegion.filter((row) => String(row.region_decimal ?? row.region) === String(regionActiva)));
    else if (view === "entidad") fetchData(`${API}/api/promocion-usuario/por-entidad`);
    else if (view === "distrito") fetchData(`${API}/api/promocion-usuario/por-distrito?entidad=${selectedEntidad.entidad}`);
    else if (view === "municipio" && modo === "municipios") fetchData(`${API}/api/promocion-usuario/por-municipio`);
    else if (view === "municipio") fetchData(`${API}/api/promocion-usuario/por-municipio?entidad=${selectedEntidad.entidad}&distrito_f=${selectedDistrito.distrito_f}`);
    else if (view === "plaza" && modo === "municipios") fetchData(`${API}/api/promocion-usuario/por-plaza?entidad=${selectedEntidad.entidad}&distrito_f=${selectedDistrito.distrito_f}&cve_municipio_inegi=${encodeURIComponent(selectedMunicipio.cve_municipio_inegi)}`);
    else if (view === "plaza") fetchData(`${API}/api/promocion-usuario/por-plaza?entidad=${selectedEntidad.entidad}&distrito_f=${selectedDistrito.distrito_f}${selectedMunicipio?.cve_municipio_inegi ? `&cve_municipio_inegi=${encodeURIComponent(selectedMunicipio.cve_municipio_inegi)}` : ""}`);
    else if (view === "seccion") fetchData(`${API}/api/promocion-usuario/por-seccion-plaza?plaza_id=${selectedUsuario.plaza_id}${selectedMunicipio?.cve_municipio_inegi ? `&cve_municipio_inegi=${encodeURIComponent(selectedMunicipio.cve_municipio_inegi)}` : ""}`);
    else fetchData(`${API}/api/promocion-usuario/por-usuario?entidad=${selectedEntidad.entidad}&distrito_f=${selectedDistrito.distrito_f}`);
  };
  const handleSort = (col) => { if (sortBy === col) setSortDir(d => d === "asc" ? "desc" : "asc"); else { setSortBy(col); setSortDir("desc"); } };

  const todayPct = (row) => Number(row?.meta_diaria || 0) > 0
    ? (Number(row?.conv_hoy || 0) / Number(row.meta_diaria)) * 100
    : 0;
  const withoutAffiliateTotals = (row) => {
    const normalized = { ...row };
    if (row.conv_normales !== undefined && row.conv_normales !== null) {
      const totalSinAfiliados = Number(row.conv_normales || 0)
        + Number(row.conv_credencial || 0)
        + (SHOW_PLAIN_AFFILIATES ? Number(row.conv_afiliado || 0) : 0);
      const meta = Number(row.meta || 0);
      normalized.convencidos_total = totalSinAfiliados;
      normalized.conv_total_tipos = totalSinAfiliados;
      normalized.pct_convencidos = meta > 0 ? (totalSinAfiliados / meta) * 100 : 0;
      normalized.pendientes = Math.max(meta - totalSinAfiliados, 0);
    }
    [1, 2, 3, 4, 5, 6, 7].forEach((day) => {
      const casaXCasaDia = row[`dia_${day}_norm`];
      if (casaXCasaDia !== undefined && casaXCasaDia !== null) {
        normalized[`dia_${day}`] = Number(casaXCasaDia || 0)
          + Number(row[`dia_${day}_cred`] || 0)
          + (SHOW_PLAIN_AFFILIATES ? Number(row[`dia_${day}_afil`] || 0) : 0);
      }
    });
    return normalized;
  };
  const filtered = data.map(withoutAffiliateTotals).filter(r => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (r.entidad_nombre || "").toLowerCase().includes(s)
      || (r.entidades_nombres || "").toLowerCase().includes(s)
      || String(r.region_nombre || r.region_decimal || "").toLowerCase().includes(s)
      || String(r.circunscripcion || "").toLowerCase().includes(s)
      || (r.distrito_nombre || "").toLowerCase().includes(s)
      || (r.municipio_nombre || "").toLowerCase().includes(s)
      || (r.cve_municipio_inegi || "").toLowerCase().includes(s)
      || (r.nombre_completo || "").toLowerCase().includes(s)
      || (r.uid || "").toLowerCase().includes(s);
  }).sort((a, b) => {
    const av = sortBy === "pct_meta_hoy" ? todayPct(a) : a[sortBy];
    const bv = sortBy === "pct_meta_hoy" ? todayPct(b) : b[sortBy];
    const an = Number(av), bn = Number(bv);
    const numeric = !isNaN(an) && !isNaN(bn);
    let cmp = numeric ? an - bn : String(av || "").localeCompare(String(bv || ""), "es");
    return sortDir === "desc" ? -cmp : cmp;
  });

  const vals = filtered.map(r => Number(r.pct_convencidos || 0)).filter(v => v > 0);
  const minVal = vals.length ? Math.min(...vals) : 0;
  const maxVal = vals.length ? Math.max(...vals) : 100;
  const maxTodayPct = Math.max(...data.map(todayPct), 0.0001);

  const [kpis, setKpis] = useState(null);
  const [filtroOperacion, setFiltroOperacion] = useState("general");
  const [kpisLoading, setKpisLoading] = useState(false);
  const [kpisError, setKpisError] = useState("");

  useEffect(() => {
    if (aclLoading) return;
    if (canOperacionHoy && !canOperacionUltimoDia) setAvanceEsHoy(true);
    if (!canOperacionHoy && canOperacionUltimoDia) setAvanceEsHoy(false);
  }, [aclLoading, canOperacionHoy, canOperacionUltimoDia]);

  const fetchKpis = useCallback(async () => {
    setKpisLoading(true);
    setKpisError("");
    setKpis(null);
    try {
      let url = `${API}/api/promocion-usuario/kpis`;
      const params = [`tipo_actividad=${filtroOperacion}`];
      if (view === "distrito" && selectedEntidad?.entidad) {
        params.push(`entidad=${selectedEntidad.entidad}`);
      } else if (view === "municipio" && selectedEntidad?.entidad && selectedDistrito?.distrito_f) {
        params.push(`entidad=${selectedEntidad.entidad}`, `distrito_f=${selectedDistrito.distrito_f}`);
      } else if ((view === "plaza" || view === "seccion" || view === "mapa") && selectedEntidad?.entidad && selectedDistrito?.distrito_f) {
        params.push(`entidad=${selectedEntidad.entidad}`, `distrito_f=${selectedDistrito.distrito_f}`);
        if (selectedMunicipio?.cve_municipio_inegi) params.push(`cve_municipio_inegi=${encodeURIComponent(selectedMunicipio.cve_municipio_inegi)}`);
        if ((view === "seccion" || view === "mapa") && selectedUsuario?.plaza_id) params.push(`plaza_id=${selectedUsuario.plaza_id}`);
      } else if ((view === "plaza" || view === "seccion" || view === "mapa") && modo === "municipios" && selectedEntidad?.entidad && selectedMunicipio?.cve_municipio_inegi) {
        params.push(`entidad=${selectedEntidad.entidad}`, `cve_municipio_inegi=${encodeURIComponent(selectedMunicipio.cve_municipio_inegi)}`);
        if ((view === "seccion" || view === "mapa") && selectedUsuario?.plaza_id) params.push(`plaza_id=${selectedUsuario.plaza_id}`);
      } else if (view === "entidad" && tipoTerritorio === "CIRCUNSCRIPCIONAL" && perfil?.circunscripcion) {
        params.push(`circunscripcion=${perfil.circunscripcion}`);
      }
      if (params.length) url += `?${params.join("&")}`;
      const res = await fetch(url, { headers });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setKpis(await res.json());
    } catch (e) {
      console.error("Error cargando indicadores de promoción:", e);
      setKpisError("No se pudieron cargar los indicadores. Intenta nuevamente.");
    }
    finally { setKpisLoading(false); }
  }, [token, view, selectedEntidad, selectedDistrito, selectedMunicipio, selectedUsuario, filtroOperacion]);

  useEffect(() => { fetchKpis(); }, [fetchKpis]);

  const totalMeta = data.reduce((s, r) => s + Number(r.meta || 0), 0);
  const totalConvTotal = data.reduce((s, r) => s + Number(r.convencidos_total || 0), 0);
  const totalConvHoy = data.reduce((s, r) => s + Number(r.conv_hoy || 0), 0);
  const totalFaltaronHoy = data.reduce((s, r) => s + Number(r.faltaron_hoy || 0), 0);
  const totalVisitasHoy = data.reduce((s, r) => s + Number(r.visitas_hoy || 0), 0);
  const totalPendientes = data.reduce((s, r) => s + Number(r.pendientes || 0), 0);
  const totalDetalleConvencidos = Number(kpis?.conv_normales || 0)
    + Number(kpis?.conv_credencial || 0)
    + (SHOW_PLAIN_AFFILIATES ? Number(kpis?.conv_afiliado || 0) : 0);
  const totalConvencidosVisible = totalDetalleConvencidos;
  const pendientesVisibles = Math.max(Number(kpis?.meta_total || 0) - totalConvencidosVisible, 0);
  const pctAvanceVisible = Number(kpis?.meta_total || 0) > 0
    ? ((totalConvencidosVisible / Number(kpis.meta_total)) * 100).toFixed(2)
    : "0.00";
  const detallePct = (value) => totalDetalleConvencidos > 0
    ? ((Number(value || 0) / totalDetalleConvencidos) * 100).toFixed(1)
    : "0.0";
  const pctFaltante = Number(kpis?.meta_total || 0) > 0
    ? ((pendientesVisibles / Number(kpis.meta_total)) * 100).toFixed(2)
    : "0.00";
  const pctGlobal = totalMeta > 0 ? ((totalConvTotal / totalMeta) * 100).toFixed(1) : 0;
  const pctAvanceUltimoDia = Number(kpis?.meta_diaria || 0) > 0
    ? ((Number(kpis?.convencidos_ayer || 0) / Number(kpis.meta_diaria)) * 100).toFixed(1)
    : 0;
  const cotsActivosDia = avanceEsHoy ? kpis?.cots_con_visita_hoy : kpis?.cots_con_visita_ayer;
  const pctCotsActivos = Number(kpis?.rutas_asignadas || 0) > 0
    ? ((Number(cotsActivosDia || 0) / Number(kpis.rutas_asignadas)) * 100).toFixed(1)
    : 0;
  const pctExitoUltimoDia = Number(kpis?.visitas_ayer || 0) > 0
    ? ((Number(kpis?.convencidos_ayer || 0) / Number(kpis.visitas_ayer)) * 100).toFixed(1)
    : 0;
  const pctAvanceHoy = Number(kpis?.meta_diaria || 0) > 0
    ? ((Number(kpis?.convencidos_hoy || 0) / Number(kpis.meta_diaria)) * 100).toFixed(1)
    : 0;
  const pctExitoHoy = Number(kpis?.visitas_hoy || 0) > 0
    ? ((Number(kpis?.convencidos_hoy || 0) / Number(kpis.visitas_hoy)) * 100).toFixed(1)
    : 0;
  const fechaAyer = (() => {
    const fecha = new Date();
    fecha.setDate(fecha.getDate() - (fecha.getDay() === 1 ? 2 : 1));
    return fecha;
  })();
  const fechaOperativaBackend = kpis?.fecha_ultimo_operativo
    ? (() => {
        const [year, month, day] = kpis.fecha_ultimo_operativo.split("-").map(Number);
        return new Date(year, month - 1, day);
      })()
    : fechaAyer;
  const fechaUltimoDia = new Intl.DateTimeFormat("es-MX", {
    day: "numeric", month: "long", year: "numeric"
  }).format(fechaOperativaBackend);
  const fechaHoy = new Intl.DateTimeFormat("es-MX", {
    day: "numeric", month: "long", year: "numeric"
  }).format(new Date());

  const entidadNombre = selectedEntidad?.entidad_nombre || (data.length > 0 ? data[0].entidad_nombre : "");
  const title = view === "region" ? "Reporte de Promoción — Lista de Regiones"
    : view === "circunscripcion" ? "Reporte de Promoción — Lista de Circunscripciones"
    : view === "entidad" && vistaInicial === "circunscripcion" && circunscripcionActiva
      ? `Reporte de Promoción — Circunscripción ${circunscripcionActiva} — Lista de Entidades`
    : view === "entidad" && vistaInicial === "region" && regionActiva != null ? `Reporte de Promoción — Región ${regionActiva} — Lista de Entidades`
    : view === "entidad" ? "Reporte de Promoción — Lista de Entidades"
    : view === "distrito" ? (modo === "distrital" ? "Reporte de Promoción — Lista de Distritos" : `Reporte de Promoción — ${entidadNombre}`)
    : view === "municipio" && modo === "municipios" ? "Reporte de Promoción — Lista de Municipios"
    : view === "municipio" ? `Reporte de Promoción — ${entidadNombre} — Dto. ${selectedDistrito?.distrito_f} — Municipios`
    : view === "plaza" && modo === "municipios" ? `Reporte de Promoción — ${entidadNombre} — ${selectedMunicipio?.municipio_nombre || ""} — Rutas`
    : view === "plaza" ? `Reporte de Promoción — ${entidadNombre} — Dto. ${selectedDistrito?.distrito_f} — ${selectedMunicipio?.municipio_nombre ? `${selectedMunicipio.municipio_nombre} — ` : ""}Rutas`
    : view === "seccion" ? `${selectedUsuario?.nombre_cot || selectedUsuario?.plaza} — Ruta: ${selectedUsuario?.ruta || ''}`
    : view === "mapa" ? `Visitas — ${selectedUsuario?.nombre_completo || selectedUsuario?.uid}`
    : `Reporte de Promoción — ${entidadNombre} — Dto. ${selectedDistrito?.distrito_f} — COTs`;
  const ultimaActualizacion = kpis?.ultima_actualizacion
    ? new Intl.DateTimeFormat("es-MX", { dateStyle: "short", timeStyle: "short" }).format(new Date(kpis.ultima_actualizacion))
    : null;

  const goToReportRoot = () => {
    setSearch(""); setSelectedEntidad(null); setSelectedDistrito(null); setSelectedMunicipio(null); setSelectedUsuario(null);
    if (vistaInicial === "circunscripcion") { setView("circunscripcion"); setCircunscripcionActiva(null); fetchData(`${API}/api/promocion-usuario/por-circunscripcion`); }
    else if (vistaInicial === "region") { setView("region"); setRegionActiva(null); fetchRegiones(); }
    else if (vistaInicial === "distrito") { setView("distrito"); fetchData(`${API}/api/promocion-usuario/por-distrito`); }
    else if (vistaInicial === "municipio") { setView("municipio"); fetchData(`${API}/api/promocion-usuario/por-municipio`); }
    else { setView("entidad"); fetchData(`${API}/api/promocion-usuario/por-entidad`); }
  };
  const rutaTabla = [
    { label: vistaInicial === "circunscripcion" ? "Circunscripciones" : vistaInicial === "region" ? "Regiones" : vistaInicial === "distrito" ? "Distritos" : vistaInicial === "municipio" ? "Municipios" : "Entidades", onClick: goToReportRoot },
    ...(regionActiva != null && view !== "region" ? [{ label: `Región ${regionActiva}`, onClick: () => { setView("entidad"); setSearch(""); setSelectedEntidad(null); setSelectedDistrito(null); setSelectedMunicipio(null); setData(entidadesRegion.filter((row) => String(row.region_decimal ?? row.region) === String(regionActiva))); } }] : []),
    ...(circunscripcionActiva && view !== "circunscripcion" ? [{ label: `Circunscripción ${circunscripcionActiva}`, onClick: () => { setView("entidad"); setSearch(""); setSelectedEntidad(null); setSelectedDistrito(null); setSelectedMunicipio(null); fetchData(`${API}/api/promocion-usuario/por-entidad?circunscripcion=${encodeURIComponent(circunscripcionActiva)}`); } }] : []),
    ...(selectedEntidad?.entidad && view !== "entidad" ? [{ label: selectedEntidad.entidad_nombre || entidadNombre || `Entidad ${selectedEntidad.entidad}`, onClick: () => { setView("distrito"); setSearch(""); setSelectedDistrito(null); setSelectedMunicipio(null); setSelectedUsuario(null); fetchData(`${API}/api/promocion-usuario/por-distrito?entidad=${selectedEntidad.entidad}`); } }] : []),
    ...(selectedDistrito?.distrito_f && !["entidad", "distrito"].includes(view) ? [{ label: `Dto. ${selectedDistrito.distrito_f}`, onClick: () => { setDistritoView("rutas"); setView("plaza"); setSearch(""); setSelectedMunicipio(null); setSelectedUsuario(null); fetchData(`${API}/api/promocion-usuario/por-plaza?entidad=${selectedEntidad.entidad}&distrito_f=${selectedDistrito.distrito_f}`); } }] : []),
    ...(selectedMunicipio?.municipio_nombre && ["plaza", "seccion", "mapa"].includes(view) ? [{ label: selectedMunicipio.municipio_nombre, onClick: () => { setView("plaza"); setSearch(""); setSelectedUsuario(null); const districtParam = selectedDistrito?.distrito_f ? `&distrito_f=${selectedDistrito.distrito_f}` : ""; fetchData(`${API}/api/promocion-usuario/por-plaza?entidad=${selectedEntidad.entidad}${districtParam}&cve_municipio_inegi=${encodeURIComponent(selectedMunicipio.cve_municipio_inegi)}`); } }] : []),
    ...(selectedUsuario && ["seccion", "mapa"].includes(view) ? [{ label: selectedUsuario.nombre_cot || selectedUsuario.nombre_completo || selectedUsuario.plaza || selectedUsuario.uid, onClick: () => { setView("seccion"); setSearch(""); fetchData(`${API}/api/promocion-usuario/por-seccion-plaza?plaza_id=${selectedUsuario.plaza_id}${selectedMunicipio?.cve_municipio_inegi ? `&cve_municipio_inegi=${encodeURIComponent(selectedMunicipio.cve_municipio_inegi)}` : ""}`); } }] : []),
  ];

  const df = (n) => { const dt = new Date(); dt.setDate(dt.getDate() - n); return new Intl.DateTimeFormat("es-MX", { day: "numeric", month: "long" }).format(dt); };
  const dias7 = [1,2,3,4,5,6,7].flatMap(n => [
    {k:`dia_${n}`,l:`Total ${df(n)}`,a:"right"},
    {k:`dia_${n}_norm`,l:`Casa x Casa ${df(n)}`,a:"right"},
    {k:`dia_${n}_cred`,l:`Afil. con Credencial ${df(n)}`,a:"right"}
  ]);

  const circCols = [
    {k:"circunscripcion",l:"Circ.",a:"center"},
    {k:"entidades_nombres",l:"Entidades"},
    {k:"total_secciones",l:"Secciones",a:"right"},
    {k:"total_plazas",l:"Rutas",a:"right"},
    {k:"rutas_asignadas",l:"COTs asignados a ruta",a:"right"},
    {k:"plazas_disponibles",l:"Rutas sin asignación",a:"right"},
    {k:"meta",l:"Meta de convencidos",a:"right"},
    {k:"convencidos_total",l:"Convencidos",a:"right"},
    {k:"pct_convencidos",l:"% Avance Convencidos",a:"center"},
    {k:"conv_normales",l:"Casa x Casa",a:"right"},
    {k:"conv_afiliado",l:"Afiliados",a:"right"},
    {k:"conv_credencial",l:"Afiliados con Credencial",a:"right"},
    {k:"conv_total_tipos",l:"Total",a:"right"},
    {k:"pendientes",l:"Pendientes por Convencer",a:"right"},
    {k:"meta_diaria",l:"Meta Diaria (15 por ruta asignada)",a:"right"},
    {k:"visitas_hoy",l:"Visitas",a:"right"},
    {k:"conv_hoy",l:"Convencidos",a:"right"},
    {k:"pct_meta_hoy",l:"% de meta diaria",a:"center"},
    {k:"faltaron_hoy",l:"Faltantes",a:"right"},
    ...dias7,
  ];

  const rawCols = view === "region"
    ? [{k:"region_decimal",l:"Región",a:"center"},{k:"entidades",l:"Entidades",a:"right"}, ...circCols.slice(2)]
    : view === "circunscripcion"
    ? circCols
    : view === "entidad"
    ? [{k:"circunscripcion",l:"Circ.",a:"center"},{k:"region_decimal",l:"Región",a:"center"},{k:"entidad_nombre",l:"Entidad"},{k:"total_secciones",l:"Secciones",a:"right"},{k:"total_plazas",l:"Rutas",a:"right"},{k:"rutas_asignadas",l:"COTs asignados a ruta",a:"right"},{k:"plazas_disponibles",l:"Rutas sin asignación",a:"right"},{k:"meta",l:"Meta de convencidos",a:"right"},{k:"convencidos_total",l:"Convencidos",a:"right"},{k:"pct_convencidos",l:"% Avance Convencidos",a:"center"},{k:"conv_normales",l:"Convencidos",a:"right"},{k:"conv_credencial",l:"Entrega credencial",a:"right"},{k:"conv_afiliado",l:"Afiliados",a:"right"},{k:"conv_total_tipos",l:"Total",a:"right"},{k:"pendientes",l:"Pendientes por Convencer",a:"right"},{k:"meta_diaria",l:"Meta Diaria (15 por ruta asignada)",a:"right"},{k:"visitas_hoy",l:"Visitas",a:"right"},{k:"conv_hoy",l:"Convencidos",a:"right"},{k:"pct_meta_hoy",l:"% de meta diaria",a:"center"},{k:"faltaron_hoy",l:"Faltantes",a:"right"}, ...dias7]
    : view === "municipio" && modo === "municipios"
    ? [{k:"circunscripcion",l:"Circ.",a:"center"},{k:"region_decimal",l:"Región",a:"center"},{k:"entidad_nombre",l:"Entidad"},{k:"distrito_f",l:"Dto. Federal",a:"center"},{k:"municipio_nombre",l:"Municipio"},{k:"total_secciones",l:"Secciones",a:"right"},{k:"meta",l:"Meta de convencidos",a:"right"},{k:"convencidos_total",l:"Convencidos",a:"right"},{k:"pct_convencidos",l:"% Avance Convencidos",a:"center"},{k:"conv_normales",l:"Casa x Casa",a:"right"},{k:"conv_credencial",l:"Entrega credencial",a:"right"},{k:"conv_total_tipos",l:"Total",a:"right"},{k:"pendientes",l:"Pendientes por Convencer",a:"right"},{k:"visitas_hoy",l:"Visitas",a:"right"},{k:"conv_hoy_norm",l:"Casa x Casa",a:"right"},{k:"conv_hoy_cred",l:"Afiliados con Credencial",a:"right"},{k:"conv_hoy_total",l:"Total",a:"right"}, ...dias7]
    : view === "municipio"
    ? [{k:"entidad_nombre",l:"Entidad"},{k:"distrito_f",l:"Dto. Federal",a:"center"},{k:"municipio_nombre",l:"Municipio"},{k:"total_secciones",l:"Secciones",a:"right"},{k:"meta",l:"Meta de convencidos",a:"right"},{k:"convencidos_total",l:"Convencidos",a:"right"},{k:"pct_convencidos",l:"% Avance Convencidos",a:"center"},{k:"conv_normales",l:"Casa x Casa",a:"right"},{k:"conv_credencial",l:"Entrega credencial",a:"right"},{k:"conv_total_tipos",l:"Total",a:"right"},{k:"pendientes",l:"Pendientes por Convencer",a:"right"},{k:"visitas_hoy",l:"Visitas",a:"right"},{k:"conv_hoy_norm",l:"Casa x Casa",a:"right"},{k:"conv_hoy_cred",l:"Afiliados con Credencial",a:"right"},{k:"conv_hoy_total",l:"Total",a:"right"}, ...dias7]
    : view === "distrito"
    ? [{k:"entidad_nombre",l:"Entidad"},{k:"distrito_f",l:"Dto.",a:"center"},{k:"distrito_nombre",l:"Cabecera"},{k:"total_secciones",l:"Secciones",a:"right"},{k:"total_plazas",l:"Rutas",a:"right"},{k:"rutas_asignadas",l:"COTs asignados a ruta",a:"right"},{k:"plazas_disponibles",l:"Rutas sin asignación",a:"right"},{k:"meta",l:"Meta de convencidos",a:"right"},{k:"convencidos_total",l:"Convencidos",a:"right"},{k:"pct_convencidos",l:"% Avance Convencidos",a:"center"},{k:"conv_normales",l:"Convencidos",a:"right"},{k:"conv_credencial",l:"Entrega credencial",a:"right"},{k:"conv_afiliado",l:"Afiliados",a:"right"},{k:"conv_total_tipos",l:"Total",a:"right"},{k:"pendientes",l:"Pendientes por Convencer",a:"right"},{k:"meta_diaria",l:"Meta Diaria (15 por ruta asignada)",a:"right"},{k:"visitas_hoy",l:"Visitas",a:"right"},{k:"conv_hoy",l:"Convencidos",a:"right"},{k:"pct_meta_hoy",l:"% de meta diaria",a:"center"},{k:"faltaron_hoy",l:"Faltantes",a:"right"}, ...dias7]
    : view === "plaza"
    ? (() => {
      const d = (n) => { const dt = new Date(); dt.setDate(dt.getDate() - n); return `Conv ${String(dt.getDate()).padStart(2,'0')}/${String(dt.getMonth()+1).padStart(2,'0')}/${String(dt.getFullYear()).slice(2)}`; };
      return [{k:"plaza",l:"RUTA"},{k:"nombre_cot",l:"COT Asignado"},{k:"telefono",l:"Teléfono"},{k:"fecha_actualizacion",l:"Fecha actualización",a:"center"},{k:"ocupado",l:"Ruta ocupada",a:"center"},{k:"secciones_ruta",l:"# Secciones",a:"right"},{k:"ruta",l:"Secciones de Ruta"},{k:"meta",l:"Meta de convencidos",a:"right"},{k:"convencidos_total",l:"Convencidos",a:"right"},{k:"pct_convencidos",l:"% Avance Convencidos",a:"center"},{k:"conv_normales",l:"Casa x Casa",a:"right"},{k:"conv_afiliado",l:"Afiliados",a:"right"},{k:"conv_credencial",l:"Afiliados con Credencial",a:"right"},{k:"conv_total_tipos",l:"Total",a:"right"},{k:"pendientes",l:"Pendientes por Convencer",a:"right"},{k:"meta_diaria",l:"Meta Diaria (15)",a:"right"},{k:"visitas_hoy",l:"Visitas",a:"right"},{k:"conv_hoy",l:"Convencidos",a:"right"},{k:"pct_meta_hoy",l:"% de meta diaria",a:"center"},{k:"faltaron_hoy",l:"Faltantes",a:"right"}, ...dias7];
    })()
    : view === "seccion"
    ? [{k:"seccion",l:"Sección"},{k:"meta",l:"Meta de convencidos",a:"right"},{k:"convencidos_total",l:"Convencidos",a:"right"},{k:"pct_convencidos",l:"% Avance Convencidos",a:"center"},{k:"conv_normales",l:"Convencidos",a:"right"},{k:"conv_credencial",l:"Entrega credencial",a:"right"},{k:"conv_afiliado",l:"Afiliados",a:"right"},{k:"conv_total_tipos",l:"Total",a:"right"},{k:"pendientes",l:"Pendientes por Convencer",a:"right"},{k:"conv_hoy",l:"Convencidos Hoy",a:"right"}, ...dias7]
    : (() => {
      const d = (n) => { const dt = new Date(); dt.setDate(dt.getDate() - n); return `Conv ${String(dt.getDate()).padStart(2,'0')}/${String(dt.getMonth()+1).padStart(2,'0')}/${String(dt.getFullYear()).slice(2)}`; };
      return [{k:"nombre_completo",l:"Nombre"},{k:"telefono",l:"Teléfono"},{k:"meta",l:"Meta",a:"right"},{k:"convencidos_total",l:"Convencidos",a:"right"},{k:"conv_en_ruta",l:"En Ruta",a:"right"},{k:"conv_fuera_ruta",l:"Fuera Ruta",a:"right"},{k:"pct_convencidos",l:"% Avance",a:"center"},{k:"dia_1",l:d(1),a:"right"},{k:"dia_2",l:d(2),a:"right"},{k:"dia_3",l:d(3),a:"right"},{k:"dia_4",l:d(4),a:"right"},{k:"dia_5",l:d(5),a:"right"},{k:"dia_6",l:d(6),a:"right"},{k:"dia_7",l:d(7),a:"right"}];
    })();

  const rutaDistrictLabel = view === "plaza"
    ? splitRouteName(data.find((row) => splitRouteName(row.plaza))?.plaza)?.prefix
    : "";

  const reorderReportColumns = (source) => {
    const result = [...source];
    const reorder = (keys) => {
      const positions = result.map((column, index) => keys.includes(column.k) ? index : -1).filter((index) => index >= 0);
      if (positions.length !== keys.length) return;
      const byKey = Object.fromEntries(positions.map((index) => [result[index].k, result[index]]));
      keys.forEach((key, index) => { result[positions[index]] = byKey[key]; });
    };
    reorder(["conv_normales", "conv_afiliado", "conv_credencial", "conv_total_tipos"]);
    [1,2,3,4,5,6,7].forEach((day) => reorder([`dia_${day}`, `dia_${day}_norm`, `dia_${day}_afil`, `dia_${day}_cred`]));
    return result;
  };
  const affiliateColumnKeys = new Set([
    "conv_afiliado",
    ...[1, 2, 3, 4, 5, 6, 7].map((day) => `dia_${day}_afil`)
  ]);
  const tableColumnPermissions = {
    total_secciones: "promueve.avance.tabla.secciones",
    total_plazas: "promueve.avance.tabla.rutastotal",
    rutas_asignadas: "promueve.avance.tabla.rutasactivas",
    plazas_disponibles: "promueve.avance.tabla.rutaslibres",
    meta: "promueve.avance.tabla.meta",
    convencidos_total: "promueve.avance.tabla.avancetotal",
    pct_convencidos: "promueve.avance.tabla.avanceporcentaje",
    conv_normales: "promueve.avance.tabla.detalleavance",
    conv_afiliado: "promueve.avance.tabla.detalleavance",
    conv_credencial: "promueve.avance.tabla.detalleavance",
    conv_total_tipos: "promueve.avance.tabla.detalleavance",
    pendientes: "promueve.avance.tabla.pendientestotal",
    meta_diaria: "promueve.avance.tabla.metadiaria",
    visitas_hoy: "promueve.avance.tabla.detallehoy",
    conv_hoy: "promueve.avance.tabla.detallehoy",
    pct_meta_hoy: "promueve.avance.tabla.detallehoy",
    faltaron_hoy: "promueve.avance.tabla.detallehoy",
    conv_hoy_norm: "promueve.avance.tabla.detallehoy",
    conv_hoy_cred: "promueve.avance.tabla.detallehoy",
    conv_hoy_afil: "promueve.avance.tabla.detallehoy",
    conv_hoy_total: "promueve.avance.tabla.detallehoy",
  };
  const canTableColumn = (key) => {
    if (["nombre_cot", "nombre_completo", "telefono"].includes(key)) return canCotsContact;
    const permission = /^dia_\d+(?:_(norm|cred|afil))?$/.test(key)
      ? "promueve.avance.tabla.semana"
      : tableColumnPermissions[key];
    return !permission || aclLoading || acl.can(permission);
  };
  const cols = reorderReportColumns(rawCols).filter((column) => (
    (SHOW_PLAIN_AFFILIATES || !affiliateColumnKeys.has(column.k)) && canTableColumn(column.k)
  )).map((column) => {
    const labels = {
      meta: "Meta de convencidos",
      convencidos_total: "Convencidos",
      pct_convencidos: "% Avance",
      conv_normales: "Casa x Casa",
      conv_afiliado: "Afiliados",
      conv_credencial: "Afiliados con Credencial",
      pendientes: "Pendientes por Convencer",
      meta_diaria: "Meta diaria",
      conv_hoy: "Convencidos Hoy",
      conv_cot: "Conv. por COT en Ruta",
      conv_cot_fuera: "Conv. por COT Fuera Ruta",
      conv_en_ruta: "En Ruta",
      conv_fuera_ruta: "Fuera Ruta",
      ritmo_necesario: "Conv/Día p/Meta",
    };
    const dayMatch = column.k.match(/^dia_\d+_(norm|afil|cred)$/);
    const dayLabel = dayMatch
      ? `${{ norm: "Casa x Casa", afil: "Afiliados", cred: "Afiliados con Credencial" }[dayMatch[1]]} ${df(Number(column.k.match(/^dia_(\d+)/)[1]))}`
      : null;
    return { ...column, l: dayLabel || labels[column.k] || column.l };
  });

  const hasTerritorialContextColumns = ["distrito", "municipio", "plaza"].includes(view);
  const isNationalMunicipalityView = view === "municipio" && modo === "municipios";
  const stickyColumnCount = view === "circunscripcion" ? 2 : view === "region" ? 1 : view === "plaza" ? 3 : isNationalMunicipalityView ? 5 : hasTerritorialContextColumns ? 3 : view === "entidad" && tipoTerritorio === "NACIONAL" ? 3 : 2;
  const compactNationalColumns = view === "entidad" && tipoTerritorio === "NACIONAL";
  const districtStickyLeft = [0, 190, 280];
  const districtStickyWidth = [190, 90, 190];
  const municipalityStickyLeft = [0, 70, 150, 340, 430];
  const municipalityStickyWidth = [70, 80, 190, 90, 190];
  const plazaStickyLeft = [0, 130, 350];
  const plazaStickyWidth = [130, 220, 140];
  const stickyCellSx = (index, backgroundColor, isHeader = false) => index < stickyColumnCount ? {
    position: "sticky",
    left: view === "plaza" ? plazaStickyLeft[index] : isNationalMunicipalityView ? municipalityStickyLeft[index] : hasTerritorialContextColumns ? districtStickyLeft[index] : index === 0 ? 0 : index === 1 ? (compactNationalColumns ? 70 : 110) : (compactNationalColumns ? 140 : 300),
    zIndex: isHeader ? 4 : 3,
    minWidth: view === "plaza" ? plazaStickyWidth[index] : isNationalMunicipalityView ? municipalityStickyWidth[index] : hasTerritorialContextColumns ? districtStickyWidth[index] : compactNationalColumns && index < 2 ? 70 : index === 0 ? 110 : 190,
    width: view === "plaza" ? plazaStickyWidth[index] : isNationalMunicipalityView ? municipalityStickyWidth[index] : hasTerritorialContextColumns ? districtStickyWidth[index] : compactNationalColumns && index < 2 ? 70 : undefined,
    maxWidth: view === "plaza" ? plazaStickyWidth[index] : isNationalMunicipalityView ? municipalityStickyWidth[index] : hasTerritorialContextColumns ? districtStickyWidth[index] : compactNationalColumns && index < 2 ? 70 : undefined,
    backgroundColor,
    boxShadow: index === stickyColumnCount - 1 ? "4px 0 6px -4px rgba(0,0,0,0.35)" : undefined
  } : {};
  const dayColumnMatch = (key) => key.match(/^dia_(\d+)(?:_(norm|cred|afil))?$/);
  const hasDayGroups = cols.some((c) => dayColumnMatch(c.k));
  const convGroupKeys = ["conv_normales", "conv_afiliado", "conv_credencial", "conv_total_tipos"];
  const isConvGroupColumn = (key) => convGroupKeys.includes(key);
  const hasConvGroups = cols.some((c) => isConvGroupColumn(c.k));
  const todayGroupKeys = ["visitas_hoy", "conv_hoy", "pct_meta_hoy", "faltaron_hoy", "conv_hoy_norm", "conv_hoy_cred", "conv_hoy_afil", "conv_hoy_total"];
  const isTodayGroupColumn = (key) => todayGroupKeys.includes(key);
  const hasTodayGroups = cols.filter((c) => todayGroupKeys.includes(c.k)).length > 1;
  const isActiveTodayGroupColumn = (key) => hasTodayGroups && isTodayGroupColumn(key);
  const todayVisibleKeys = cols.filter((c) => isTodayGroupColumn(c.k)).map((c) => c.k);
  const hasGroupedHeaders = hasDayGroups || hasConvGroups || hasTodayGroups;
  const dayGroupColumnCount = (day) => cols.filter((c) => dayColumnMatch(c.k)?.[1] === String(day)).length;
  const convGroupColumnCount = cols.filter((c) => isConvGroupColumn(c.k)).length;
  const dayGroupSx = (key) => {
    const match = dayColumnMatch(key);
    if (!match) return {};
    return {
      borderLeft: !match[2] ? "2px solid #c7c7c7" : undefined,
      borderRight: match[2] === "cred" ? "2px solid #c7c7c7" : undefined
    };
  };
  const convGroupSx = (key) => {
    if (!isConvGroupColumn(key)) return {};
    return {
    borderLeft: key === convGroupKeys[0] ? "2px solid #c7c7c7" : undefined,
    borderRight: key === convGroupKeys[convGroupKeys.length - 1] ? "2px solid #c7c7c7" : undefined
    };
  };
  const todayGroupSx = (key) => {
    if (!isActiveTodayGroupColumn(key)) return {};
    return {
      borderLeft: key === todayVisibleKeys[0] ? "2px solid #c7c7c7" : undefined,
      borderRight: key === todayVisibleKeys[todayVisibleKeys.length - 1] ? "2px solid #c7c7c7" : undefined
    };
  };
  const dayDateLabel = (key) => {
    const match = dayColumnMatch(key);
    const dt = new Date();
    dt.setDate(dt.getDate() - Number(match?.[1] || 0));
    return new Intl.DateTimeFormat("es-MX", { day: "numeric", month: "long" }).format(dt);
  };
  const dayMetricLabel = (key) => {
    const match = dayColumnMatch(key);
    if (!match || !match[2]) return "Total";
    return { norm: "Casa x Casa", afil: "Afiliados", cred: "Afiliados con Credencial" }[match[2]];
  };
  const renderColumnLabel = (key, label) => (
    <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.25 }}>
      {label}
      {key === "convencidos_total" && <Tooltip arrow placement="top" title="Este total suma los registros obtenidos en la actividad de Promueve y los afiliados que ya recibieron su credencial."><InfoIcon sx={{ fontSize: 16, cursor: "help" }} /></Tooltip>}
      {key === "pct_convencidos" && <Tooltip arrow placement="top" title="Porcentaje de avance de convencidos respecto a la meta."><InfoIcon sx={{ fontSize: 16, cursor: "help" }} /></Tooltip>}
      {key === "conv_normales" && <Tooltip arrow placement="top" title={<>Registros obtenidos de la actividad de Promueve.<br /><br />Si se registra un afiliado que ya cuente con credencial, aparecerá contemplado en esta columna, pero se restará de la siguiente: “Afiliados con credencial”.</>}><InfoIcon sx={{ fontSize: 16, cursor: "help" }} /></Tooltip>}
      {key === "meta_diaria" && <Tooltip arrow placement="top" title={<>La meta diaria se calcula multiplicando el número de rutas con un COT responsable asignado por 15 convencidos esperados por día.<br /><br />Las rutas sin COT responsable asignado aportan 0 a la meta diaria. Por eso no se incluyen en este cálculo hasta que tengan una persona responsable.</>}><InfoIcon sx={{ fontSize: 16, cursor: "help" }} /></Tooltip>}
    </Box>
  );

  const tabPermissions = {
    circunscripciones: "promueve.avance.tabs.xcc",
    regiones: "promueve.avance.tabs.xregiones",
    entidades: "promueve.avance.tabs.xentidades",
    distritos: "promueve.avance.tabs.xdistritos",
    municipios: "promueve.avance.tabs.xmunicipios",
    rutas: "promueve.avance.tabs.xrutas",
  };
  const canReportTab = (value) => aclLoading || acl.can(tabPermissions[value]);
  const nationalReportTabs = [
    { value: "circunscripciones", label: "Circunscripciones" },
    { value: "regiones", label: "Regiones" },
    { value: "entidades", label: "Entidades" },
    { value: "distritos", label: "Distritos" },
    { value: "municipios", label: "Municipios" },
  ].filter((tab) => canReportTab(tab.value));
  const reportTabs = selectedMunicipio && ["plaza", "seccion", "mapa"].includes(view)
    ? [{ value: "rutas", label: "Rutas" }].filter((tab) => canReportTab(tab.value))
    : selectedDistrito && ["municipio", "plaza"].includes(view)
    ? [{ value: "rutas", label: "Rutas" }, { value: "municipios", label: "Municipios" }].filter((tab) => canReportTab(tab.value))
    : circunscripcionActiva && view === "entidad"
    ? [{ value: "entidades", label: "Entidades" }].filter((tab) => canReportTab(tab.value))
    : regionActiva != null && view === "entidad"
    ? [{ value: "entidades", label: "Entidades" }].filter((tab) => canReportTab(tab.value))
    : selectedEntidad && view === "distrito"
    ? [{ value: "distritos", label: "Distritos" }].filter((tab) => canReportTab(tab.value))
    : nationalReportTabs;
  const activeReportTab = selectedMunicipio && ["plaza", "seccion", "mapa"].includes(view)
    ? "rutas"
    : selectedDistrito && ["municipio", "plaza"].includes(view)
    ? (view === "plaza" ? "rutas" : "municipios")
    : circunscripcionActiva && view === "entidad"
    ? "entidades"
    : regionActiva != null && view === "entidad"
    ? "entidades"
    : selectedEntidad && view === "distrito"
    ? "distritos"
    : view === "circunscripcion" ? "circunscripciones"
    : view === "region" ? "regiones"
    : view === "entidad" ? "entidades"
    : view === "distrito" ? "distritos"
    : view === "municipio" ? "municipios"
    : "entidades";
  const handleReportTabChange = (_, value) => {
    if (!value || value === activeReportTab) return;
    setSearch("");
    setSelectedEntidad(null);
    setSelectedDistrito(null);
    setSelectedMunicipio(null);
    setSelectedUsuario(null);
    setCircunscripcionActiva(null);
    setRegionActiva(null);
    if (value === "rutas" && selectedDistrito) {
      setSelectedDistrito(selectedDistrito);
      setSelectedEntidad(selectedEntidad);
      setView("plaza");
      setDistritoView("rutas");
      fetchData(`${API}/api/promocion-usuario/por-plaza?entidad=${selectedEntidad.entidad}&distrito_f=${selectedDistrito.distrito_f}`);
      return;
    }
    if (value === "municipios" && selectedDistrito) {
      setSelectedEntidad(selectedEntidad);
      setSelectedDistrito(selectedDistrito);
      setView("municipio");
      setDistritoView("municipios");
      fetchData(`${API}/api/promocion-usuario/por-municipio?entidad=${selectedEntidad.entidad}&distrito_f=${selectedDistrito.distrito_f}`);
      return;
    }
    if (value === "circunscripciones") {
      setModo("circunscripcion"); setVistaInicial("circunscripcion"); setView("circunscripcion");
      fetchData(`${API}/api/promocion-usuario/por-circunscripcion`);
    } else if (value === "regiones") {
      setModo("region"); setVistaInicial("region"); setView("region"); fetchRegiones();
    } else if (value === "entidades") {
      setModo("territorio"); setVistaInicial("entidad"); setView("entidad");
      fetchData(`${API}/api/promocion-usuario/por-entidad`);
    } else if (value === "distritos") {
      setModo("distrital"); setVistaInicial("distrito"); setView("distrito");
      fetchData(`${API}/api/promocion-usuario/por-distrito`);
    } else if (value === "municipios") {
      setModo("municipios"); setVistaInicial("municipio"); setView("municipio");
      fetchData(`${API}/api/promocion-usuario/por-municipio`);
    }
  };

  return (
    <Box sx={{ width: "100%", mx: "auto", px: { xs: 1, sm: 2, md: 3 }, py: 2 }}>
      <Backdrop
        open={loading || kpisLoading}
        sx={{ zIndex: (theme) => theme.zIndex.drawer + 10, color: "#fff", flexDirection: "column", gap: 2 }}
      >
        <CircularProgress size={54} thickness={4} sx={{ color: "#fff" }} />
        <Typography variant="body1" fontWeight={700}>Procesando reporte…</Typography>
      </Backdrop>
      <Box sx={{ display: "flex", alignItems: "center", mb: 2, gap: 1, flexWrap: "wrap" }}>
        {view !== vistaInicial && <IconButton onClick={handleBack} sx={{ color: "#AF272F" }}><ArrowBackIcon /></IconButton>}
        <Typography variant="h5" sx={{ color: "#AF272F", fontWeight: 700, flexGrow: 1 }}>{title}</Typography>
        {ultimaActualizacion && <Chip size="small" variant="outlined" label={`Última actualización: ${ultimaActualizacion}`} sx={{ color: "#666", borderColor: "#bdbdbd", bgcolor: "#fafafa" }} />}
        <IconButton onClick={handleRefresh} title="Actualizar"><RefreshIcon /></IconButton>
        {canDescargaExcel && <Button size="small" startIcon={<DownloadIcon />} onClick={() => exportXLSX(
          filtered.map((row) => ({ ...row, pct_meta_hoy: todayPct(row) })),
          cols,
          `promocion_usuario_${view}_${new Date().toISOString().slice(0,10)}`,
          { includeTotals: true }
        )} sx={{ color: "#AF272F" }}>Excel</Button>}
      </Box>

      <Breadcrumbs sx={{ mb: 2 }}>
        {tipoTerritorio === "NACIONAL" && <Link underline="hover" sx={{ cursor: "pointer", fontWeight: view === vistaInicial ? 700 : 400, color: view === vistaInicial ? "#AF272F" : "inherit" }} onClick={() => {
          setSearch(""); setSelectedEntidad(null); setSelectedDistrito(null);
          if (vistaInicial === "circunscripcion") { setView("circunscripcion"); setModo("circunscripcion"); setCircunscripcionActiva(null); fetchData(`${API}/api/promocion-usuario/por-circunscripcion`); }
          else if (vistaInicial === "region") { setView("region"); setModo("region"); setRegionActiva(null); fetchRegiones(); }
          else if (vistaInicial === "distrito") { setView("distrito"); fetchData(`${API}/api/promocion-usuario/por-distrito`); }
          else if (vistaInicial === "municipio") { setView("municipio"); fetchData(`${API}/api/promocion-usuario/por-municipio`); }
          else { setView("entidad"); fetchData(`${API}/api/promocion-usuario/por-entidad`); }
        }}>{vistaInicial === "circunscripcion" ? "Circunscripciones" : vistaInicial === "region" ? "Regiones" : vistaInicial === "distrito" ? "Distritos" : vistaInicial === "municipio" ? "Municipios" : "Entidades"}</Link>}
        {vistaInicial === "region" && regionActiva != null && view !== "region" && (
          view === "entidad"
            ? <Typography color="#AF272F" fontWeight={700}>Región {regionActiva}</Typography>
            : <Link underline="hover" sx={{ cursor: "pointer" }} onClick={() => { setView("entidad"); setSelectedEntidad(null); setSelectedDistrito(null); setSelectedMunicipio(null); setSearch(""); setData(entidadesRegion.filter((row) => String(row.region_decimal ?? row.region) === String(regionActiva))); }}>Región {regionActiva}</Link>
        )}
        {vistaInicial === "circunscripcion" && circunscripcionActiva && view !== "circunscripcion" && (
          view === "entidad"
            ? <Typography color="#AF272F" fontWeight={700}>Circunscripción {circunscripcionActiva}</Typography>
            : <Link underline="hover" sx={{ cursor: "pointer" }} onClick={() => {
                setView("entidad"); setSelectedEntidad(null); setSelectedDistrito(null); setSelectedMunicipio(null); setSearch("");
                fetchData(`${API}/api/promocion-usuario/por-entidad?circunscripcion=${encodeURIComponent(circunscripcionActiva)}`);
              }}>Circunscripción {circunscripcionActiva}</Link>
        )}
        {modo === "municipios" && selectedMunicipio && view !== "municipio" && (
          view === "plaza"
            ? <Typography color="#AF272F" fontWeight={700}>{selectedMunicipio.municipio_nombre}</Typography>
            : <Link underline="hover" sx={{ cursor: "pointer" }} onClick={() => { setView("plaza"); setSelectedUsuario(null); setSearch(""); fetchData(`${API}/api/promocion-usuario/por-plaza?entidad=${selectedEntidad.entidad}&cve_municipio_inegi=${encodeURIComponent(selectedMunicipio.cve_municipio_inegi)}`); }}>{selectedMunicipio.municipio_nombre}</Link>
        )}
        {modo !== "municipios" && (view === "distrito" || view === "municipio" || view === "plaza" || view === "seccion" || view === "mapa") && (tipoTerritorio === "DISTRITAL"
          ? <Typography color="inherit">{entidadNombre}</Typography>
          : <Link underline="hover" sx={{ cursor: "pointer", fontWeight: view === "distrito" ? 700 : 400, color: view === "distrito" ? "#AF272F" : "inherit" }} onClick={() => { setView("distrito"); setSelectedDistrito(null); setSelectedMunicipio(null); setSearch(""); fetchData(`${API}/api/promocion-usuario/por-distrito?entidad=${selectedEntidad.entidad}`); }}>{entidadNombre}</Link>)}
        {modo !== "municipios" && (view === "municipio" || view === "plaza" || view === "seccion" || view === "mapa") && <Link underline="hover" sx={{ cursor: "pointer", fontWeight: view === "municipio" ? 700 : 400, color: view === "municipio" ? "#AF272F" : "inherit" }} onClick={() => { setDistritoView("rutas"); setView("plaza"); setSelectedMunicipio(null); setSelectedUsuario(null); setSearch(""); fetchData(`${API}/api/promocion-usuario/por-plaza?entidad=${selectedEntidad.entidad}&distrito_f=${selectedDistrito.distrito_f}`); }}>Dto. {selectedDistrito?.distrito_f}</Link>}
        {view === "plaza" && rutaDistrictLabel && <Typography color="#9e9e9e">{rutaDistrictLabel}</Typography>}
        {modo !== "municipios" && (view === "plaza" || view === "seccion" || view === "mapa") && selectedMunicipio && <Link underline="hover" sx={{ cursor: "pointer", fontWeight: view === "plaza" ? 700 : 400, color: view === "plaza" ? "#AF272F" : "inherit" }} onClick={() => { setView("plaza"); setSelectedUsuario(null); setSearch(""); fetchData(`${API}/api/promocion-usuario/por-plaza?entidad=${selectedEntidad.entidad}&distrito_f=${selectedDistrito.distrito_f}&cve_municipio_inegi=${encodeURIComponent(selectedMunicipio.cve_municipio_inegi)}`); }}>{selectedMunicipio.municipio_nombre}</Link>}
        {view === "mapa" && <Typography color="#AF272F" fontWeight={700}>{selectedUsuario?.nombre_completo || selectedUsuario?.uid}</Typography>}
      </Breadcrumbs>

      {view !== "mapa" && (<>
      {kpisError && <Alert severity="error" sx={{ mb: 2 }} action={<Button color="inherit" size="small" onClick={fetchKpis}>Reintentar</Button>}>{kpisError}</Alert>}
      {kpis && (<>
      {/* FILA 1: Avance de convencidos */}
      {canProgreso && <Paper sx={{ mb: 2.5, p: { xs: 3, sm: 4.5, md: 6 }, minHeight: { md: 410 }, borderTop: "6px solid #AF272F" }}>
        <Box sx={{ display: "flex", gap: { xs: 4, md: 7 }, flexWrap: "wrap", minHeight: { md: 285 }, alignItems: "stretch" }}>
        <Box sx={{ flex: "1.7 1 520px", minWidth: 0 }}>
        <Typography fontWeight={700} sx={{ color: "#AF272F", mb: 2.5, fontSize: { xs: "1.75rem", sm: "2.2rem" } }}>
          Avance de convencidos
        </Typography>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 2, flexWrap: "wrap", mb: 3 }}>
          <Typography fontWeight={700} sx={{ fontSize: { xs: "2.25rem", sm: "3.2rem" }, lineHeight: 1.1 }}>
            {num(totalConvencidosVisible)} <Typography component="span" color="text.secondary" sx={{ fontSize: { xs: "1.2rem", sm: "1.65rem" }, fontWeight: 500 }}>de</Typography>{" "}
            <Typography component="span" sx={{ fontSize: "inherit", fontWeight: "inherit" }}>{num(kpis?.meta_total)}</Typography>
          </Typography>
          {canProgresoMeta && <Typography fontWeight={700} sx={{ color: "#AF272F", fontSize: { xs: "1.55rem", sm: "2.05rem" } }}>
            {pctAvanceVisible}% de avance
          </Typography>}
        </Box>
        {canProgresoMeta && <>
          <LinearProgress
            variant="determinate"
            value={Math.min(100, Math.max(0, Number(pctAvanceVisible)))}
            sx={{ height: { xs: 24, sm: 30 }, my: 1.5, borderRadius: 15, bgcolor: "#f3d7da", "& .MuiLinearProgress-bar": { bgcolor: "#AF272F", borderRadius: 15 } }}
          />
          <Box sx={{ mt: 3, display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 2, flexWrap: "wrap" }}>
            <Box sx={{ display: "flex", alignItems: "baseline", gap: { xs: 0.75, sm: 1.1 }, flexWrap: "wrap" }}>
              <Typography component="span" fontWeight={800} sx={{ color: "#4f4f4f", fontSize: { xs: "1.3rem", sm: "1.7rem" } }}>Faltan</Typography>
              <Typography component="span" fontWeight={800} sx={{ color: "#AF272F", fontSize: { xs: "1.65rem", sm: "2.15rem" } }}>{num(pendientesVisibles)}</Typography>
              <Typography component="span" fontWeight={800} sx={{ color: "#4f4f4f", fontSize: { xs: "1.3rem", sm: "1.7rem" } }}>convencidos</Typography>
            </Box>
            <Typography fontWeight={700} sx={{ color: "#AF272F", fontSize: { xs: "1.55rem", sm: "2.05rem" }, whiteSpace: "nowrap" }}>
              {pctFaltante}% faltante
            </Typography>
          </Box>
        </>}
        </Box>
        {canProgresoDetalle && <Box sx={{ flex: "1 1 420px", borderLeft: { xs: "none", md: "1px solid #e0e0e0" }, borderTop: { xs: "1px solid #e0e0e0", md: "none" }, pl: { xs: 0, md: 5 }, pt: { xs: 3, md: 0 } }}>
          <Typography fontWeight={700} sx={{ mb: 2, color: "#555", fontSize: { xs: "1.7rem", sm: "2.1rem" } }}>
            Detalle
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", py: 1.5, gap: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <HandshakeIcon sx={{ color: "#AF272F", fontSize: 40 }} />
              <Typography sx={{ fontSize: { xs: "1.05rem", sm: "1.25rem" } }}>Casa x Casa</Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "baseline", gap: 1 }}>
              <Typography fontWeight={800} sx={{ fontSize: { xs: "1.15rem", sm: "1.45rem" } }}>{num(kpis?.conv_normales)}</Typography>
              <Typography color="text.secondary" fontWeight={800} sx={{ fontSize: { xs: "1.15rem", sm: "1.45rem" } }}>{detallePct(kpis?.conv_normales)}%</Typography>
            </Box>
          </Box>
          {SHOW_PLAIN_AFFILIATES && <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", py: 1.5, gap: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <ThumbUpIcon sx={{ color: "#AF272F", fontSize: 40 }} />
              <Typography sx={{ fontSize: { xs: "1.05rem", sm: "1.25rem" } }}>Afiliados</Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "baseline", gap: 1 }}>
              <Typography fontWeight={800} sx={{ fontSize: { xs: "1.15rem", sm: "1.45rem" } }}>{num(kpis?.conv_afiliado)}</Typography>
              <Typography color="text.secondary" fontWeight={800} sx={{ fontSize: { xs: "1.15rem", sm: "1.45rem" } }}>{detallePct(kpis?.conv_afiliado)}%</Typography>
            </Box>
          </Box>}
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", py: 1.5, gap: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <BadgeIcon sx={{ color: "#AF272F", fontSize: 40 }} />
              <Typography sx={{ fontSize: { xs: "1.05rem", sm: "1.25rem" } }}>Afiliados con Credencial</Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "baseline", gap: 1 }}>
              <Typography fontWeight={800} sx={{ fontSize: { xs: "1.15rem", sm: "1.45rem" } }}>{num(kpis?.conv_credencial)}</Typography>
              <Typography color="text.secondary" fontWeight={800} sx={{ fontSize: { xs: "1.15rem", sm: "1.45rem" } }}>{detallePct(kpis?.conv_credencial)}%</Typography>
            </Box>
          </Box>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", mt: 1.5, pt: 2, borderTop: "1px solid #e0e0e0" }}>
            <Typography fontWeight={700} color="text.secondary" sx={{ fontSize: { xs: "1rem", sm: "1.2rem" } }}>Total de convencidos</Typography>
            <Typography fontWeight={800} sx={{ color: "#AF272F", fontSize: { xs: "1.65rem", sm: "2rem" } }}>{num(totalDetalleConvencidos)}</Typography>
          </Box>
        </Box>}
        </Box>
      </Paper>}
      {/* FILA 2: Operación */}
      {canOperacion && <Paper sx={{ mb: 2, p: { xs: 3, sm: 3.75 }, borderTop: "5px solid #BC955B", borderRadius: 2.5, boxShadow: "0 4px 16px rgba(0,0,0,0.08)" }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, flexWrap: "wrap", mb: 2 }}>
        <Typography variant="h6" fontWeight={700} sx={{ color: "#BC955B" }}>
          {avanceEsHoy ? "Hoy" : "Último día operativo"} — {avanceEsHoy ? fechaHoy : fechaUltimoDia}
        </Typography>
        {((avanceEsHoy && canOperacionUltimoDia) || (!avanceEsHoy && canOperacionHoy)) && <Chip
          size="small"
          label={avanceEsHoy ? "Ver último día" : "Ver hoy"}
          clickable
          onClick={() => setAvanceEsHoy((actual) => !actual)}
          sx={{ color: "#BC955B", borderColor: "#BC955B", fontWeight: 700 }}
          variant="outlined"
        />}
      </Box>
      <Box sx={{ display: "flex", gap: { xs: 2.5, md: 3 }, flexWrap: "wrap", alignItems: "stretch" }}>
      {view !== "seccion" && (canOperacionCotsActivos || canOperacionRutasActivas) && <Box sx={{ flex: "1 1 450px", minWidth: 0, minHeight: { sm: 320 }, p: { xs: 2.5, sm: 3.25 }, borderRadius: 2.5, bgcolor: "#f5fbf6", border: "1px solid #cfe4d2", boxShadow: "0 4px 14px rgba(46,125,50,0.09)" }}>
      {canOperacionCotsActivos && <Box sx={{ pb: 2 }}>
          <Typography variant="h6" fontWeight={800} sx={{ color: "#2e7d32", mb: 1.5 }}>
            COTs activos
          </Typography>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 2, flexWrap: "wrap", mb: 1.5 }}>
            <Typography variant="h5" fontWeight={700}>
              {num(cotsActivosDia)} <Typography component="span" variant="body1" color="text.secondary">COTs activos de {num(kpis?.rutas_asignadas)} en territorio</Typography>
            </Typography>
            <Typography variant="h6" fontWeight={700} sx={{ color: "#2e7d32" }}>
              {pctCotsActivos}%
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={Math.min(100, Math.max(0, Number(pctCotsActivos)))}
            sx={{ height: 12, mb: 1, borderRadius: 6, bgcolor: "#dceddf", "& .MuiLinearProgress-bar": { bgcolor: "#2e7d32", borderRadius: 6 } }}
          />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5, lineHeight: 1.4 }}>
            COTs activos, realizaron al menos una visita el {avanceEsHoy ? fechaHoy : fechaUltimoDia}.
          </Typography>
        </Box>}
        {canOperacionRutasActivas && <Box sx={{ mt: 1, pt: 2.5, borderTop: "1px solid #e0e0e0" }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 2, flexWrap: "wrap", mb: 1.5 }}>
            <Typography variant="h5" fontWeight={700}>
              {num(kpis?.rutas_asignadas)} <Typography component="span" variant="body1" color="text.secondary">rutas asignadas de {num(kpis?.total_plazas)} rutas disponibles</Typography>
            </Typography>
            <Typography variant="h6" fontWeight={700} sx={{ color: "#777773" }}>
              {kpis?.pct_ocupacion || 0}%
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={Math.min(100, Math.max(0, Number(kpis?.pct_ocupacion || 0)))}
            sx={{ height: 12, mb: 1, borderRadius: 6, bgcolor: "#f0f0ef", "& .MuiLinearProgress-bar": { bgcolor: "#777773", borderRadius: 6 } }}
          />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5, lineHeight: 1.4 }}>
            Rutas pendientes de asignar: <Typography component="span" variant="body2" fontWeight={700} sx={{ color: "#c62828" }}>{num(kpis?.rutas_faltantes)}</Typography>
          </Typography>
        </Box>}
      </Box>}
      {canOperacionAvanceMeta && (
        <AvanceDiarioCard
          kpis={kpis}
          esHoy={avanceEsHoy}
          pctAvance={avanceEsHoy ? pctAvanceHoy : pctAvanceUltimoDia}
          pctExito={avanceEsHoy ? pctExitoHoy : pctExitoUltimoDia}
          filtroOperacion={filtroOperacion}
          onFiltroOperacionChange={setFiltroOperacion}
          mostrarDistribucion={view !== "plaza" && canOperacionDistribucion}
        />
      )}
      </Box>
      </Paper>}
      </>)}
      {reportTabs.length > 0 && <Paper sx={{ mb: 2, px: { xs: 1.5, sm: 2.5 }, pt: 1.25, borderRadius: 2.5, border: "1px solid #ead0d2", boxShadow: "0 3px 12px rgba(175,39,47,0.08)" }}>
        <Typography variant="overline" sx={{ px: 1, color: "#777", fontWeight: 800, letterSpacing: 1 }}>
          Ver por
        </Typography>
        <Tabs
          value={activeReportTab}
          onChange={handleReportTabChange}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            minHeight: 46,
            "& .MuiTabs-indicator": { height: 3, borderRadius: 3, bgcolor: "#AF272F" },
            "& .MuiTab-root": { minHeight: 46, textTransform: "none", fontWeight: 700, color: "#777", px: { xs: 1.5, sm: 2.5 } },
            "& .Mui-selected": { color: "#AF272F !important" },
          }}
        >
          {reportTabs.map((tab) => <Tab key={tab.value} value={tab.value} label={tab.label} />)}
        </Tabs>
      </Paper>}

      {/* Mejores / peores resultados, ajustados al tamaño del nivel actual */}
      {filtered.length > 2 && canTops && (() => {
        const dataConVal = filtered.filter(r => Number(r.meta) > 0);
        if (dataConVal.length < 2) return null;
        const sorted = [...dataConVal].sort((a, b) => Number(b.pct_convencidos) - Number(a.pct_convencidos));
        // Repartir los elementos entre ambos bloques evita repetir filas
        // cuando el nivel actual tiene pocos registros (p. ej. Durango).
        const sideCount = Math.min(5, Math.floor(sorted.length / 2));
        const top5 = sorted.slice(0, sideCount);
        const bottom5 = sorted.slice(-sideCount).reverse();
        const getName = (r) => view === "seccion" ? `Sección ${r.seccion}` : view === "usuario" ? r.nombre_completo : view === "plaza" ? (r.nombre_cot || r.plaza) : view === "distrito" ? `Dto. ${r.distrito_f} - ${r.distrito_nombre}` : view === "municipio" ? `${r.municipio_nombre}, ${r.entidad_nombre}` : r.entidad_nombre;
        const getId = (r) => r.seccion || r.uid || r.plaza_id || `${r.entidad}-${r.distrito_f || ''}`;
        return (
          <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap" }}>
            <Paper sx={{ p: 2, flex: 1, minWidth: 280, borderTop: "4px solid #2e7d32", borderRadius: 2, boxShadow: "0 2px 8px rgba(46,125,50,0.12)" }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5, pb: 1, borderBottom: "1px solid #e0e0e0" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <EmojiEventsIcon sx={{ color: "#2e7d32" }} />
                  <Typography variant="subtitle2" fontWeight={700} sx={{ color: "#2e7d32" }}>Primeros {sideCount}</Typography>
                </Box>
                <Typography variant="caption" color="text.secondary">% avance</Typography>
              </Box>
              {top5.map((r, i) => (
                <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 1, cursor: "pointer", py: 0.65, px: 0.75, borderRadius: 1.5, bgcolor: i % 2 === 0 ? "#f1f8f2" : "#fff", "&:hover": { bgcolor: "#e8f5e9" } }}
                  onClick={() => { const id = getId(r); setHighlightId(id); setTimeout(() => { const el = document.getElementById(`row-${id}`); if(el) el.scrollIntoView({behavior:"smooth",block:"center"}); }, 100); }}>
                  <Box sx={{ width: 24, height: 24, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", bgcolor: "#2e7d32", color: "#fff", fontSize: 12, fontWeight: 700, flexShrink: 0 }}>{i + 1}</Box>
                  <Typography variant="caption" sx={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{getName(r)}</Typography>
                  <Typography variant="caption" fontWeight={700} sx={{ color: "#2e7d32" }}>{pct(r.pct_convencidos)}</Typography>
                </Box>
              ))}
            </Paper>
            <Paper sx={{ p: 2, flex: 1, minWidth: 280, borderTop: "4px solid #c62828", borderRadius: 2, boxShadow: "0 2px 8px rgba(198,40,40,0.12)" }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5, pb: 1, borderBottom: "1px solid #e0e0e0" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <TimelineIcon sx={{ color: "#c62828" }} />
                  <Typography variant="subtitle2" fontWeight={700} sx={{ color: "#c62828" }}>Últimos {sideCount}</Typography>
                </Box>
                <Typography variant="caption" color="text.secondary">% avance</Typography>
              </Box>
              {bottom5.map((r, i) => (
                <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 1, cursor: "pointer", py: 0.65, px: 0.75, borderRadius: 1.5, bgcolor: i % 2 === 0 ? "#fff5f5" : "#fff", "&:hover": { bgcolor: "#ffebee" } }}
                  onClick={() => { const id = getId(r); setHighlightId(id); setTimeout(() => { const el = document.getElementById(`row-${id}`); if(el) el.scrollIntoView({behavior:"smooth",block:"center"}); }, 100); }}>
                  <Box sx={{ width: 24, height: 24, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", bgcolor: "#c62828", color: "#fff", fontSize: 12, fontWeight: 700, flexShrink: 0 }}>{i + 1}</Box>
                  <Typography variant="caption" sx={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{getName(r)}</Typography>
                  <Typography variant="caption" fontWeight={700} sx={{ color: "#c62828" }}>{pct(r.pct_convencidos)}</Typography>
                </Box>
              ))}
            </Paper>
          </Box>
        );
      })()}
      </>)}

      {view !== "mapa" && canTabla && (
      <Box sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
        <SearchIcon sx={{ color: "text.secondary" }} />
        <TextField size="small" placeholder="Buscar..." value={search} onChange={(e) => setSearch(e.target.value)} sx={{ width: { xs: "100%", sm: 250, md: 300 } }} />
        <Chip
          size="small"
          variant="outlined"
          label={`${filtered.length.toLocaleString("es-MX")} registros mostrados`}
          sx={{ ml: "auto", fontWeight: 700, color: "#AF272F", borderColor: "#AF272F", bgcolor: "#fff8f8" }}
        />
        {canTablaAmpliar && <Button
          size="small"
          variant="outlined"
          startIcon={tablaExpandida ? <FullscreenExitIcon /> : <FullscreenIcon />}
          onClick={() => setTablaExpandida((actual) => !actual)}
          sx={{ textTransform: "none", minWidth: 0, whiteSpace: "nowrap" }}
        >
          {tablaExpandida ? "Reducir tabla" : "Ampliar tabla"}
        </Button>}
      </Box>
      )}

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading && <LinearProgress sx={{ mb: 2, "& .MuiLinearProgress-bar": { bgcolor: "#AF272F" } }} />}

      {view === "plaza" && selectedDistrito && !selectedMunicipio && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Esta vista muestra el avance acumulado del territorio asignado a cada ruta, no el desempeño individual de la persona. Si una persona fue asignada recientemente, los datos pueden corresponder a los avances registrados por quien ocupaba anteriormente esa ruta.
        </Alert>
      )}

      {view === "mapa" && (
        <Box>
          {visitasLoading && <LinearProgress sx={{ mb: 2, "& .MuiLinearProgress-bar": { bgcolor: "#AF272F" } }} />}
          <Paper sx={{ p: 1, mb: 2, display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
            <Typography variant="body2"><strong>{visitas.length}</strong> visitas registradas</Typography>
            <Typography variant="body2"><strong>{convencidos.length}</strong> convencidos (🟢)</Typography>
            <Typography variant="body2">Usuario: <strong>{selectedUsuario?.nombre_completo || selectedUsuario?.uid}</strong></Typography>
          </Paper>
          {(visitas.length > 0 || convencidos.length > 0) && (() => {
            // Agrupar visitas por ubicación (3 decimales = ~100m)
            const groups = {};
            visitas.forEach(v => {
              const key = `${Number(v.latitud).toFixed(3)},${Number(v.longitud).toFixed(3)}`;
              if (!groups[key]) groups[key] = { lat: v.latitud, lng: v.longitud, items: [], hasConv: false };
              groups[key].items.push(v);
              if (v.conv_nombre) groups[key].hasConv = true;
            });
            const markers = Object.values(groups);
            const center = markers.length > 0 ? [markers[0].lat, markers[0].lng] : convencidos.length > 0 ? [convencidos[0].latitud, convencidos[0].longitud] : [23.6, -102.5];
            const greenIcon = new L.Icon({ iconUrl: "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-green.png", shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png", iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34], shadowSize: [41, 41] });
            const blueIcon = new L.Icon({ iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png", shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png", iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34], shadowSize: [41, 41] });
            return (
            <Box sx={{ height: "65vh", borderRadius: 2, overflow: "hidden", boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}>
              <MapContainer center={center} zoom={15} maxZoom={19} style={{ height: "100%", width: "100%" }} attributionControl={false} preferCanvas={true}>
                <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" maxZoom={19} />
                {secciones.map((s, i) => (
                  <GeoJSON key={`sec-${s.seccion}`} data={JSON.parse(s.geojson)} style={{ color: "#AF272F", weight: 2, fillColor: "#AF272F", fillOpacity: 0.08 }}>
                    <Popup><strong>Sección {s.seccion}</strong></Popup>
                  </GeoJSON>
                ))}
                {markers.map((m, i) => (
                  <Marker key={i} position={[m.lat, m.lng]} icon={m.hasConv ? greenIcon : blueIcon}>
                    <Popup maxHeight={300}>
                      <div style={{ minWidth: 220, maxHeight: 300, overflowY: "auto" }}>
                        <strong>{m.items.length} visita{m.items.length > 1 ? "s" : ""}</strong>
                        {m.items.map((v, j) => (
                          <div key={j} style={{ borderTop: j > 0 ? "1px solid #ddd" : "none", paddingTop: 6, marginTop: 6 }}>
                            <span style={{ fontSize: 12 }}>📅 {v.fecha_visita?.slice(0, 10)}</span><br />
                            {v.observaciones && <em style={{ fontSize: 11 }}>{v.observaciones}</em>}
                            {v.conv_nombre && (
                              <div style={{ marginTop: 4, padding: 4, background: "#e8f5e9", borderRadius: 4, fontSize: 12 }}>
                                <strong>✅ Convencido:</strong> {v.conv_nombre} {v.conv_paterno} {v.conv_materno}
                                {v.conv_telefono && <><br />📱 {v.conv_telefono}</>}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </Popup>
                  </Marker>
                ))}
                {convencidos.map((c, i) => (
                  <Marker key={`conv-${i}`} position={[c.latitud, c.longitud]} icon={greenIcon}>
                    <Popup>
                      <div style={{ minWidth: 180 }}>
                        <strong>✅ Convencido</strong><br />
                        {c.nombre} {c.apellido_paterno} {c.apellido_materno}<br />
                        {c.telefono && <span>📱 {c.telefono}<br /></span>}
                        <span style={{ fontSize: 11, color: "#666" }}>📅 {c.fecha_creacion?.slice(0, 10)}</span>
                      </div>
                    </Popup>
                  </Marker>
                ))}
              </MapContainer>
            </Box>
            );
          })()}
          {!visitasLoading && visitas.length === 0 && convencidos.length === 0 && (
            <Alert severity="info">Este usuario no tiene visitas con ubicación registrada.</Alert>
          )}
        </Box>
      )}

      {view !== "mapa" && (
      <Paper sx={{ mb: 1.25, px: { xs: 2, sm: 3 }, py: 1.75, display: "flex", alignItems: "center", gap: 1.25, flexWrap: "wrap", borderLeft: "6px solid #AF272F", bgcolor: "#fff8f8", boxShadow: "0 2px 8px rgba(175,39,47,0.12)" }}>
        {rutaTabla.map((item, index) => (
          <React.Fragment key={`${item.label}-${index}`}>
            {index > 0 && <Typography sx={{ color: "#BC955B", fontWeight: 800, fontSize: "1.15rem" }}>›</Typography>}
            <Chip
              label={item.label}
              size="small"
              clickable={index < rutaTabla.length - 1}
              onClick={index < rutaTabla.length - 1 ? item.onClick : undefined}
              title={index < rutaTabla.length - 1 ? `Regresar a ${item.label}` : "Ubicación actual"}
              sx={{
                bgcolor: index === rutaTabla.length - 1 ? "#AF272F" : "#f1e4e5",
                color: index === rutaTabla.length - 1 ? "#fff" : "#6f1a20",
                fontWeight: 700,
                textTransform: "uppercase",
                cursor: index < rutaTabla.length - 1 ? "pointer" : "default",
                "&:hover": index < rutaTabla.length - 1 ? { bgcolor: "#ead0d2", color: "#AF272F" } : {},
              }}
            />
          </React.Fragment>
        ))}
      </Paper>
      )}

      {view !== "mapa" && canTabla && (
      <TableContainer component={Paper} sx={{
        maxHeight: tablaExpandida ? "100vh" : "65vh",
        height: tablaExpandida ? "100vh" : "auto",
        overflowX: "auto",
        borderRadius: tablaExpandida ? 0 : 2,
        boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
        ...(tablaExpandida ? { position: "fixed", inset: 0, zIndex: 1300, p: 1, bgcolor: "#fff" } : {})
      }}>
        {ultimaActualizacion && (
          <Typography
            component="div"
            variant="body2"
            color="text.secondary"
            sx={{ px: 2, py: 1.5, pr: tablaExpandida ? { xs: 2, sm: 20 } : 2, pt: tablaExpandida ? { xs: 6, sm: 1.5 } : 1.5, bgcolor: "background.paper", borderBottom: "1px solid", borderColor: "divider" }}
          >
            Última actualización de la información de la tabla: <Box component="span" sx={{ fontWeight: 700 }}>{ultimaActualizacion}</Box>
          </Typography>
        )}
        {tablaExpandida && canTablaAmpliar && (
          <Button
            size="small"
            variant="contained"
            startIcon={<FullscreenExitIcon />}
            onClick={() => setTablaExpandida(false)}
            sx={{ position: "fixed", top: 8, right: 16, zIndex: 1302, textTransform: "none", bgcolor: "#AF272F" }}
          >
            Reducir tabla
          </Button>
        )}
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              {hasGroupedHeaders ? cols.map((c, index) => {
                const match = dayColumnMatch(c.k);
                if (match) {
                  if (match[2]) return null;
                  return <TableCell key={`grupo-${c.k}`} align="center" colSpan={dayGroupColumnCount(match[1])} sx={{ ...headerSx, minWidth: 180, top: 0, borderLeft: "2px solid #c7c7c7", borderRight: "2px solid #c7c7c7" }}>
                    {dayDateLabel(c.k)}
                  </TableCell>;
                }
                if (isConvGroupColumn(c.k)) {
                  if (c.k !== convGroupKeys[0]) return null;
                  return <TableCell key="grupo-convencidos" align="center" colSpan={convGroupColumnCount} sx={{ ...headerSx, minWidth: 180, top: 0, borderLeft: "2px solid #c7c7c7", borderRight: "2px solid #c7c7c7" }}>
                    Detalle de convencidos
                  </TableCell>;
                }
                if (isActiveTodayGroupColumn(c.k)) {
                  if (c.k !== todayGroupKeys[0]) return null;
                  return <TableCell key="grupo-hoy" align="center" colSpan={todayVisibleKeys.length} sx={{ ...headerSx, minWidth: 280, top: 0, borderLeft: "2px solid #c7c7c7", borderRight: "2px solid #c7c7c7" }}>
                    Hoy
                  </TableCell>;
                }
                return <TableCell key={c.k} rowSpan={2} align={c.a || "left"} sx={{ ...headerSx, minWidth: 70, top: 0, ...stickyCellSx(index, headerSx.bgcolor, true) }}>
                  <TableSortLabel active={sortBy===c.k} direction={sortBy===c.k?sortDir:"asc"} onClick={()=>handleSort(c.k)} sx={{ ...sortSx, whiteSpace: "nowrap" }}>
                    {renderColumnLabel(c.k, c.l)}
                  </TableSortLabel>
                </TableCell>;
              }) : cols.map((c, index) => (
                <TableCell key={c.k} align={c.a || "left"} sx={{ ...headerSx, minWidth: 70, ...stickyCellSx(index, headerSx.bgcolor, true) }}>
                <TableSortLabel active={sortBy===c.k} direction={sortBy===c.k?sortDir:"asc"} onClick={()=>handleSort(c.k)} sx={{ ...sortSx, whiteSpace: "nowrap" }}>
                  {renderColumnLabel(c.k, c.l)}
                </TableSortLabel>
                </TableCell>
              ))}
              {view === "plaza" && selectedDistrito && !selectedMunicipio && (canTablaMapaRuta || canTablaAvanceGrafica) && <TableCell rowSpan={hasGroupedHeaders ? 2 : 1} align="center" sx={headerSx}>Acciones</TableCell>}
            </TableRow>
            {hasGroupedHeaders && <TableRow>
              {cols.filter((c) => dayColumnMatch(c.k) || isConvGroupColumn(c.k) || isActiveTodayGroupColumn(c.k)).map((c) => (
                <TableCell key={c.k} align={c.a || "left"} sx={{ ...headerSx, minWidth: 70, top: 37, ...dayGroupSx(c.k), ...convGroupSx(c.k), ...todayGroupSx(c.k) }}>
                  <TableSortLabel active={sortBy===c.k} direction={sortBy===c.k?sortDir:"asc"} onClick={()=>handleSort(c.k)} sx={{ ...sortSx, whiteSpace: "nowrap" }}>{renderColumnLabel(c.k, dayColumnMatch(c.k) ? dayMetricLabel(c.k) : c.l)}</TableSortLabel>
                </TableCell>
              ))}
            </TableRow>}
          </TableHead>
          <TableBody>
            {filtered.map((row, i) => {
              const pctVal = Number(row.pct_convencidos || 0);
              const color = getColor(pctVal, minVal, maxVal);
              const aggregateRoute = view === "plaza" && ["Rutas activas", "Rutas vacantes", "SECCIONES HISTÓRICAS"].includes(row.plaza);
              const clickable = !aggregateRoute && (view === "region" || view === "circunscripcion" || view === "entidad" || view === "distrito" || view === "municipio" || view === "plaza" || (view === "seccion" && row.uid));
              const rowId = row.seccion || row.uid || row.plaza_id || `${row.entidad}-${row.distrito_f || ''}`;
              const rowBg = highlightId === rowId ? "#fff9c4" : i % 2 === 0 ? "#fff" : "#f5f5f5";
              return (
                <TableRow key={i} hover={clickable} id={`row-${rowId}`}
                  onClick={() => { if (view === "region") handleRegionClick(row); else if (view === "circunscripcion") handleCircunscripcionClick(row); else if (view === "entidad") handleEntidadClick(row); else if (view === "distrito") handleDistritoClick(row); else if (view === "municipio") handleMunicipioClick(row); else if (view === "plaza") handlePlazaClick(row); }}
                  sx={{...(clickable ? { cursor: "pointer", "&:hover": { bgcolor: "#fce4ec" } } : {}), bgcolor: rowBg, transition: "background 0.5s"}}>
                  {cols.map((c, index) => (
                    <TableCell key={c.k} align={c.a || "left"} sx={{ minWidth: 70, ...dayGroupSx(c.k), ...convGroupSx(c.k), ...todayGroupSx(c.k), ...stickyCellSx(index, rowBg) }}>
                      {(c.k === "pct_convencidos") ? (
                        <Chip label={pct(row[c.k])} size="small" sx={{ bgcolor: color, color: "#fff", fontWeight: 700, fontSize: "0.75rem", minWidth: 65 }} />
                      ) : c.k === "pct_meta_hoy" ? (
                        <Chip label={`${todayPct(row).toFixed(1)}%`} size="small" sx={{ bgcolor: getColor(todayPct(row), 0, maxTodayPct), color: "#fff", fontWeight: 700, fontSize: "0.75rem", minWidth: 65 }} />
                      ) : c.k === "ocupado" ? (row[c.k] ? '✅' : '❌')
                      : c.k === "plaza" ? (splitRouteName(row[c.k])?.number || row[c.k] || "")
                      : c.k === "fecha_actualizacion" ? (
                        row[c.k] ? <Tooltip title={fechaCompleta(row[c.k])} arrow><span>{fechaRelativa(row[c.k])}</span></Tooltip> : ""
                      )
                      : ["meta","convencidos_total","conv_cot","total_usuarios","conv_en_ruta","conv_fuera_ruta","total_plazas","plazas_disponibles","secciones_ruta","meta_diaria","conv_hoy","conv_hoy_norm","conv_hoy_cred","conv_hoy_afil","conv_hoy_total","faltaron_hoy","ritmo_necesario","visitas_hoy","pendientes","total_secciones","region_decimal","conv_normales","conv_credencial","conv_afiliado","conv_total_tipos","dia_1_norm","dia_1_cred","dia_1_afil","dia_2_norm","dia_2_cred","dia_2_afil","dia_3_norm","dia_3_cred","dia_3_afil","dia_4_norm","dia_4_cred","dia_4_afil","dia_5_norm","dia_5_cred","dia_5_afil","dia_6_norm","dia_6_cred","dia_6_afil","dia_7_norm","dia_7_cred","dia_7_afil","dia_1","dia_2","dia_3","dia_4","dia_5","dia_6","dia_7"].includes(c.k)
                        ? num(c.k === "conv_normales" ? Number(row.conv_normales || 0) + Number(row.conv_afiliado || 0) : c.k === "conv_hoy_norm" ? Number(row.conv_hoy_norm || 0) + Number(row.conv_hoy_afil || 0) : /^dia_\d+$/.test(c.k) ? (Object.prototype.hasOwnProperty.call(row, `${c.k}_norm`) ? Number(row[`${c.k}_norm`] || 0) + Number(row[`${c.k}_afil`] || 0) + Number(row[`${c.k}_cred`] || 0) : Number(row[c.k] || 0)) : /^dia_\d+_norm$/.test(c.k) ? Number(row[c.k] || 0) + Number(row[c.k.replace(/_norm$/, "_afil")] || 0) : row[c.k]) : row[c.k]}
                    </TableCell>
                  ))}
                  {view === "plaza" && selectedDistrito && !selectedMunicipio && (canTablaMapaRuta || canTablaAvanceGrafica) && (
                    <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>
                      {canTablaMapaRuta && row.uid && <Tooltip title="Ver Mapa"><IconButton size="small" onClick={(e) => { e.stopPropagation(); openMapa(row); }} sx={{ color: "#1565c0" }}><MapIcon fontSize="small" /></IconButton></Tooltip>}
                      {canTablaAvanceGrafica && Number(row.plaza_id) > 0 && <Tooltip title="Gráfica Convencidos"><IconButton size="small" onClick={(e) => { e.stopPropagation(); openGrafica(row); }} sx={{ color: "#2e7d32" }}><TimelineIcon fontSize="small" /></IconButton></Tooltip>}
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
            {!loading && filtered.length === 0 && (
              <TableRow><TableCell colSpan={cols.length} align="center" sx={{ py: 4, color: "text.secondary" }}>Sin datos</TableCell></TableRow>
            )}
            {filtered.length > 0 && (
              <TableRow sx={{
                position: "sticky",
                bottom: 0,
                zIndex: 5,
                bgcolor: "#f5f5f5",
                "& > td": { height: 58, minWidth: 70, py: 2 }
              }}>
                {cols.map((c, index) => {
                  if (c.k === "entidad_nombre" || c.k === "entidades_nombres" || c.k === "distrito_nombre" || c.k === "plaza" || c.k === "nombre_completo") return <TableCell key={c.k} sx={{ fontWeight: 700, ...dayGroupSx(c.k), ...convGroupSx(c.k), ...todayGroupSx(c.k), ...stickyCellSx(index, "#f5f5f5") }}>{["entidad_nombre","entidades_nombres","plaza","nombre_completo"].includes(c.k) ? "TOTAL" : ""}</TableCell>;
                  if (c.k === "distrito_f" || c.k === "nombre_cot" || c.k === "ruta" || c.k === "telefono" || c.k === "fecha_actualizacion") return <TableCell key={c.k} sx={{ ...dayGroupSx(c.k), ...convGroupSx(c.k), ...todayGroupSx(c.k), ...stickyCellSx(index, "#f5f5f5") }}></TableCell>;
                   if (c.k === "ocupado") return <TableCell key={c.k} sx={{ ...dayGroupSx(c.k), ...convGroupSx(c.k), ...todayGroupSx(c.k), ...stickyCellSx(index, "#f5f5f5") }}></TableCell>;
                   if (c.k === "rutas_asignadas") return <TableCell key={c.k} align="right" sx={{ fontWeight: 700, ...dayGroupSx(c.k), ...convGroupSx(c.k), ...todayGroupSx(c.k), ...stickyCellSx(index, "#f5f5f5") }}>{num(filtered.reduce((s,r) => s + Number(r[c.k]||0), 0))}</TableCell>;
                  if (c.k === "pct_convencidos") { const t = filtered.reduce((s,r) => s + Number(r.meta||0), 0); const cv = filtered.reduce((s,r) => s + Number(r.convencidos_total||0), 0); return <TableCell key={c.k} align="center" sx={{ fontWeight: 700, ...dayGroupSx(c.k), ...convGroupSx(c.k), ...todayGroupSx(c.k), ...stickyCellSx(index, "#f5f5f5") }}>{t > 0 ? pct(cv/t*100) : "0%"}</TableCell>; }
                  if (c.k === "pct_meta_hoy") { const meta = filtered.reduce((s,r) => s + Number(r.meta_diaria||0), 0); const conv = filtered.reduce((s,r) => s + Number(r.conv_hoy||0), 0); const totalPct = meta > 0 ? (conv / meta) * 100 : 0; return <TableCell key={c.k} align="center" sx={{ fontWeight: 700, ...dayGroupSx(c.k), ...convGroupSx(c.k), ...todayGroupSx(c.k), ...stickyCellSx(index, "#f5f5f5") }}><Chip label={`${totalPct.toFixed(1)}%`} size="small" sx={{ bgcolor: getColor(totalPct, 0, maxTodayPct), color: "#fff", fontWeight: 700, fontSize: "0.75rem", minWidth: 65 }} /></TableCell>; }
                  if (/^dia_\d+$/.test(c.k)) { const total = filtered.reduce((s, r) => s + (Object.prototype.hasOwnProperty.call(r, `${c.k}_norm`) ? Number(r[`${c.k}_norm`] || 0) + Number(r[`${c.k}_afil`] || 0) + Number(r[`${c.k}_cred`] || 0) : Number(r[c.k] || 0)), 0); return <TableCell key={c.k} align="right" sx={{ fontWeight: 700, ...dayGroupSx(c.k), ...convGroupSx(c.k), ...todayGroupSx(c.k), ...stickyCellSx(index, "#f5f5f5") }}>{num(total)}</TableCell>; }
                  if (["meta","convencidos_total","conv_cot","total_usuarios","conv_en_ruta","conv_fuera_ruta","total_plazas","plazas_disponibles","secciones_ruta","meta_diaria","conv_hoy","conv_hoy_norm","conv_hoy_cred","conv_hoy_total","faltaron_hoy","ritmo_necesario","visitas_hoy","pendientes","total_secciones","conv_normales","conv_credencial","conv_afiliado","conv_total_tipos","dia_1_norm","dia_1_cred","dia_1_afil","dia_2_norm","dia_2_cred","dia_2_afil","dia_3_norm","dia_3_cred","dia_4_norm","dia_4_cred","dia_4_afil","dia_5_norm","dia_5_cred","dia_5_afil","dia_6_norm","dia_6_cred","dia_6_afil","dia_7_norm","dia_7_cred","dia_7_afil","dia_1","dia_2","dia_3","dia_4","dia_5","dia_6","dia_7"].includes(c.k)) return <TableCell key={c.k} align="right" sx={{ fontWeight: 700, ...dayGroupSx(c.k), ...convGroupSx(c.k), ...todayGroupSx(c.k), ...stickyCellSx(index, "#f5f5f5") }}>{num(filtered.reduce((s,r) => s + (c.k === "conv_normales" ? Number(r.conv_normales || 0) + Number(r.conv_afiliado || 0) : c.k === "conv_hoy_norm" ? Number(r.conv_hoy_norm || 0) + Number(r.conv_hoy_afil || 0) : /^dia_\d+_norm$/.test(c.k) ? Number(r[c.k] || 0) + Number(r[c.k.replace(/_norm$/, "_afil")] || 0) : Number(r[c.k] || 0)), 0))}</TableCell>;
                  return <TableCell key={c.k} sx={{ ...dayGroupSx(c.k), ...convGroupSx(c.k), ...todayGroupSx(c.k), ...stickyCellSx(index, "#f5f5f5") }}></TableCell>;
                })}
                {view === "plaza" && selectedDistrito && !selectedMunicipio && (canTablaMapaRuta || canTablaAvanceGrafica) && <TableCell></TableCell>}
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      )}

      {/* Modal Mapa */}
      <Dialog open={modalMapa} onClose={() => { setModalMapa(false); setVisitas([]); setConvencidos([]); setSecciones([]); }} fullScreen>
        <DialogTitle sx={{ bgcolor: "#AF272F", color: "#fff", display: "flex", alignItems: "center" }}>
          🗺️ Mapa — {modalUsuario?.nombre_completo || modalUsuario?.uid}
          <IconButton onClick={() => { setModalMapa(false); setVisitas([]); setConvencidos([]); setSecciones([]); }} aria-label="Cerrar mapa" sx={{ ml: "auto", color: "#fff" }}>✕</IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0, flex: 1, minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}>
          {visitasLoading && <LinearProgress sx={{ "& .MuiLinearProgress-bar": { bgcolor: "#AF272F" } }} />}
          {!visitasLoading && visitas.length === 0 && convencidos.length === 0 && (
            <Alert severity="info" sx={{ mt: 2 }}>Sin visitas con ubicación registrada.</Alert>
          )}
          {!visitasLoading && (visitas.length > 0 || convencidos.length > 0 || secciones.length > 0) && (() => {
            const { features: routeSections, bounds: sectionBounds } = getRouteSections(secciones);
            if (!sectionBounds) return <Alert severity="info" sx={{ m: 2 }}>No hay geometría disponible para las secciones de este registro.</Alert>;
            const groups = {};
            visitas.forEach(v => {
              const key = `${Number(v.latitud)},${Number(v.longitud)}`;
              if (!groups[key]) groups[key] = { lat: v.latitud, lng: v.longitud, items: [] };
              groups[key].items.push(v);
            });
            const markers = Object.values(groups);
            const greenIcon = new L.Icon({ iconUrl: "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-green.png", shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png", iconSize: [18, 30], iconAnchor: [9, 30], popupAnchor: [0, -25], shadowSize: [30, 30] });
            return (
              <MapContainer bounds={sectionBounds} boundsOptions={{ padding: [32, 32], maxZoom: 18 }} maxZoom={19} style={{ height: "100%", width: "100%", flex: 1 }} attributionControl={false} preferCanvas={true}>
                <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" maxZoom={19} />
                {routeSections.map(s => (
                  <GeoJSON key={`sec-${s.seccion}`} data={s.data} style={{ color: "#AF272F", weight: 2, fillColor: "#AF272F", fillOpacity: 0.08 }}>
                    <Popup><strong>Sección {s.seccion}</strong></Popup>
                  </GeoJSON>
                ))}
                <RouteMapMarkers bounds={sectionBounds} points={[
                  ...markers.map((m, i) => ({
                    id: `visita-${i}`,
                    position: [m.lat, m.lng],
                    popup: <div style={{ maxHeight: 200, overflow: "auto" }}>{m.items.map((v, j) => (
                      <div key={j} style={{ borderTop: j > 0 ? "1px solid #ddd" : "none", paddingTop: 4, marginTop: 4, fontSize: 12 }}>
                        📅 {v.fecha_visita?.slice(0, 10)}
                        {v.conv_nombre && <div style={{ background: "#e8f5e9", borderRadius: 4, padding: 3, marginTop: 3 }}>✅ {v.conv_nombre} {v.conv_paterno}</div>}
                      </div>
                    ))}</div>,
                  })),
                  ...convencidos.map((c, i) => ({
                    id: `convencido-${i}`,
                    position: [c.latitud, c.longitud],
                    icon: greenIcon,
                    popup: <><strong>✅</strong> {c.nombre} {c.apellido_paterno}<br/><span style={{ fontSize: 11 }}>📅 {c.fecha_creacion?.slice(0, 10)}</span></>,
                  })),
                ]} />
              </MapContainer>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Modal Gráfica */}
      <Dialog open={modalGrafica} onClose={() => setModalGrafica(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ bgcolor: "#AF272F", color: "#fff", display: "flex", alignItems: "center" }}>
          📈 Convencidos por Día — Ruta {modalUsuario?.plaza}
          <IconButton onClick={() => setModalGrafica(false)} sx={{ ml: "auto", color: "#fff" }}>✕</IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 2, pt: 4, overflow: "visible" }}>
          <Box sx={{ display: "flex", gap: 1, mb: 2, mt: 1, alignItems: "center", flexWrap: "wrap" }}>
            <TextField size="small" type="date" label="Desde" value={chartFechaInicio} onChange={e => setChartFechaInicio(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ width: 150 }} />
            <TextField size="small" type="date" label="Hasta" value={chartFechaFin} onChange={e => setChartFechaFin(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ width: 150 }} />
            <Button size="small" variant="outlined" disabled={chartLoading} onClick={() => fetchChartData(modalUsuario?.plaza_id, chartFechaInicio, chartFechaFin)} sx={{ color: "#AF272F", borderColor: "#AF272F" }}>Filtrar</Button>
            <Button size="small" disabled={chartLoading} onClick={() => { setChartFechaInicio(""); setChartFechaFin(""); fetchChartData(modalUsuario?.plaza_id, "", ""); }}>Limpiar</Button>
          </Box>
          {chartLoading ? (
            <LinearProgress aria-label="Cargando gráfica" />
          ) : chartError ? (
            <Alert severity="error" sx={{ mt: 2 }}>{chartError}</Alert>
          ) : chartData.length === 0 ? (
            <Alert severity="info" sx={{ mt: 2 }}>Sin datos de convencidos por día.</Alert>
          ) : (
            <ResponsiveContainer width="100%" height={350}>
              <LineChart data={chartData} margin={{ top: 10, right: 20, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="fecha" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <RTooltip />
                <Line type="monotone" dataKey="convencidos" stroke="#2e7d32" strokeWidth={2} dot={{ r: 3 }} name="Convencidos" label={{ position: "top", fontSize: 11, fill: "#2e7d32" }} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}

export default ReportesPromueve;
